import React, { useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import {
  Header, PaymentCard, SupportCard, ST, fmtBaht, fmtPrice, countryName, flagEmoji, useT, LANGS, readLang,
} from './Chrome';
import { SiteFooterV2 } from './Footers';
import TmuPhoneVerify from './TmuPhoneVerify';
import { fmtTmu, tmuPhoneKind, TMU_FALLBACK_LIMITS } from './tmuUtil';

///////////////////////////////////////////////////////////////////////////////
// Account pages in the new shell: /login, /orders, /wallet, /numbers.
// They speak to the legacy APIs (auth, orders, phonenumbers) but render like
// the rest of the new storefront — hero band, main + rail, cards.
///////////////////////////////////////////////////////////////////////////////

const MT = (typeof window !== 'undefined' && window.__MT) || {};

const COMMON_PASSWORDS = new Set([
  '000000','111111','123123','123456','1234567','12345678','123456789',
  '654321','666666','abc123','dragon','iloveyou','letmein','monkey',
  'passw0rd','password','password1','princess','qwerty','qwerty123',
  'shadow','soccer','sunshine','superman','trustno1','welcome',
]);
function isCommonPassword(pwd) { return COMMON_PASSWORDS.has(String(pwd).toLowerCase()); }

// Turnstile: renders the managed widget and provides the current token.
// Falls back to reCAPTCHA v3 if Turnstile is not configured.
export function useTurnstile(containerId) {
  const [token, setToken] = useState(null);
  const widgetIdRef = React.useRef(null);
  const rendered = React.useRef(false);

  useEffect(() => {
    const siteKey = MT.turnstileSiteKey;
    if (!siteKey || rendered.current) return;

    function render() {
      if (!window.turnstile || rendered.current) return;
      const el = document.getElementById(containerId);
      if (!el) return;
      rendered.current = true;
      widgetIdRef.current = window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (t) => setToken(t),
        'expired-callback': () => setToken(null),
        'error-callback': () => setToken(null),
        appearance: 'interaction-only',
      });
    }

    if (window.turnstile) { render(); return; }
    // Poll briefly for the async script to load
    const iv = setInterval(() => { if (window.turnstile) { clearInterval(iv); render(); } }, 200);
    return () => clearInterval(iv);
  }, [containerId]);

  const reset = () => {
    setToken(null);
    if (widgetIdRef.current != null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { token, reset };
}

function recaptchaToken(action) {
  return new Promise((resolve) => {
    const key = MT.recaptchaKey;
    if (!key || !window.grecaptcha || !window.grecaptcha.ready) return resolve(null);
    try {
      window.grecaptcha.ready(() => {
        window.grecaptcha.execute(key, { action }).then(resolve).catch(() => resolve(null));
      });
    } catch (e) { resolve(null); }
  });
}

async function postJson(url, body) {
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: JSON.stringify(body),
  });
  let data = null;
  try { data = await r.json(); } catch (e) { /* non-JSON error body */ }
  if (!r.ok) {
    const msg = (data && (data.message || (data.error && data.error.message))) || `HTTP ${r.status}`;
    const err = new Error(msg);
    // Server error code (e.g. not_confirmed_email) so pages can act on it, not just print it.
    err.code = (data && ((data.error && data.error.code) || data.code)) || null;
    throw err;
  }
  return data;
}

// Only same-site paths — a returnTo pointing elsewhere is dropped.
function safePath(raw) {
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : null;
}
function safeReturnTo() {
  return safePath(new URLSearchParams(window.location.search).get('returnTo')) || '/';
}

///////////////////////////////////////////////////////////////////////////////
// "Didn't get the email? Resend" — used by the sign-up success screen and by
// the login page when the account is not confirmed yet (#287). Re-sends the
// signup confirmation link and waits 30 s before allowing another send.
///////////////////////////////////////////////////////////////////////////////

const RESEND_COOLDOWN_S = 30;

function ResendConfirmation({ email }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | verified | error
  const [error, setError] = useState(null);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function resend() {
    if (state === 'sending' || wait > 0) return;
    setState('sending'); setError(null);
    try {
      await postJson('/api/auth/resend-confirmation', { email: String(email || '').trim().toLowerCase() });
      setState('sent');
      setWait(RESEND_COOLDOWN_S);
    } catch (err) {
      if (err.code === 'verified_already') { setState('verified'); return; }
      setState('error');
      setError(err.message || 'Could not resend the email. Please try again.');
    }
  }

  if (state === 'verified') {
    return (
      <p style={{ textAlign: 'center', margin: '12px 0' }}>
        This email is already confirmed. <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`}>Log in</a>
      </p>
    );
  }

  const busy = state === 'sending';
  const cooling = wait > 0;
  return (
    <div style={{ textAlign: 'center', margin: '12px 0' }}>
      {state === 'sent' && (
        <p style={{ margin: '0 0 8px' }}>Sent again to <strong>{email}</strong>. Check your spam folder too.</p>
      )}
      {state === 'error' && error && <div className="notice payerr" style={{ marginBottom: 8 }}>{error}</div>}
      <p style={{ margin: 0, color: 'var(--muted)' }}>
        Didn't get the email?{' '}
        <button type="button" disabled={busy || cooling} onClick={resend}
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: busy || cooling ? 'var(--muted)' : 'var(--teal-deep)', textDecoration: 'underline', cursor: busy || cooling ? 'default' : 'pointer' }}>
          {busy ? 'Sending…' : cooling ? `Resend in ${wait}s` : 'Resend'}
        </button>
      </p>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// "Check your email" for an account that has no password yet (#312): a
// web-checkout guest who tried to log in or sign up again. The server already
// mailed the set-password link; this offers one more send, through the
// rate-limited reset request, and carries the page's returnTo on the token.
///////////////////////////////////////////////////////////////////////////////

function SetPasswordSent({ email }) {
  const [state, setState] = useState('idle'); // idle | sending | sent | error
  const [error, setError] = useState(null);
  const [wait, setWait] = useState(RESEND_COOLDOWN_S);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function resend() {
    if (state === 'sending' || wait > 0) return;
    setState('sending'); setError(null);
    try {
      const returnTo = safeReturnTo();
      await postJson('/api/auth/request-reset-password', {
        email: String(email || '').trim().toLowerCase(),
        ...(returnTo !== '/' ? { returnTo } : {}),
      });
      setState('sent');
      setWait(RESEND_COOLDOWN_S);
    } catch (err) {
      setState('error');
      setError(err.message || 'Could not send the email. Please try again.');
    }
  }

  const busy = state === 'sending';
  const cooling = wait > 0;
  return (
    <div className="notice" style={{ textAlign: 'center' }}>
      <div>
        <b>No password yet.</b> This account was created at checkout, so there's nothing to type here.
        We emailed <strong>{email}</strong> a link to set one — it signs you in
        {safeReturnTo() !== '/' ? ' and brings you back to finish what you started.' : '.'} The link works for one hour.
      </div>
      {state === 'sent' && <p style={{ margin: '8px 0 0' }}>Sent again. Check your spam folder too.</p>}
      {state === 'error' && error && <div className="notice payerr" style={{ marginTop: 8 }}>{error}</div>}
      <p style={{ margin: '8px 0 0', color: 'var(--muted)' }}>
        Didn't get the email?{' '}
        <button type="button" disabled={busy || cooling} onClick={resend}
          style={{ background: 'none', border: 0, padding: 0, font: 'inherit', color: busy || cooling ? 'var(--muted)' : 'var(--teal-deep)', textDecoration: 'underline', cursor: busy || cooling ? 'default' : 'pointer' }}>
          {busy ? 'Sending…' : cooling ? `Send again in ${wait}s` : 'Send again'}
        </button>
      </p>
    </div>
  );
}

function fmtDate(v) {
  try {
    const d = new Date(v);
    const base = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' }).format(d);
    return base;
  } catch (e) { return String(v || ''); }
}

// Get the face-value amount the customer expects to receive (not the cost price).
function orderDisplayAmount(o) {
  const ding = o.dingData;
  const tp = o.topup && o.topup.data && o.topup.data.Price;
  if (tp && tp.ReceiveValue && tp.ReceiveCurrencyIso) {
    const val = tp.ReceiveValueExcludingTax > 0 && tp.ReceiveValueExcludingTax < tp.ReceiveValue
      ? tp.ReceiveValueExcludingTax : tp.ReceiveValue;
    return { amount: val, currency: tp.ReceiveCurrencyIso };
  }
  if (ding && ding.snd != null && ding.sndiso) {
    return { amount: Number(ding.snd), currency: ding.sndiso };
  }
  // Wondd Thai topups store face value in topupRequest.amountThb
  if (o.topupRequest && o.topupRequest.amountThb != null) {
    return { amount: o.topupRequest.amountThb, currency: 'THB' };
  }
  return { amount: o.topupAmount, currency: 'THB' };
}

export function AccountShell({ me, currency, onCurrency, active, title, sub, children, rail, hideSupportCard }) {
  return (
    <div className="page">
      <Header me={me} active={active} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero">
        <h1 className="hero-title">{title}</h1>
        {sub && <p className="hero-sub" style={{ margin: '6px 0 0' }}>{sub}</p>}
      </section>
      <div className="body">
        <main className="main">{children}</main>
        <aside className="rail">
          {rail}
          {!hideSupportCard && <SupportCard />}
        </aside>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

// me === null (guest) bounces to login; undefined = still loading.
export function useRequireAuth(me, path) {
  useEffect(() => {
    if (me === null) window.location.replace(`/login?returnTo=${encodeURIComponent(path)}`);
  }, [me, path]);
}

function StatusChip({ status }) {
  const s = String(status || '').toLowerCase();
  const good = ['fulfilled', 'paid', 'success', 'complete', 'completed'].some((k) => s.includes(k));
  const bad = ['fail', 'refund', 'cancel', 'error'].some((k) => s.includes(k));
  const icon = good ? '✓' : bad ? '✕' : '◷';
  return <span className={`tag ${good ? 'good' : bad ? 'bad' : 'dim'}`}><span className="tag-ico">{icon}</span> {status || '—'}</span>;
}

// Compact icon actions with native tooltips
function IconLink({ href, title, external, children }) {
  return (
    <a
      className="icon-btn" href={href} title={title} aria-label={title}
      {...(external ? { target: '_blank', rel: 'noreferrer' } : {})}
    >
      {children}
    </a>
  );
}

const ReceiptIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3h14v18l-2.5-1.6L14 21l-2-1.4L10 21l-2.5-1.6L5 21z" />
    <path d="M9 8h6M9 12h6" />
  </svg>
);

const RepeatIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 12a9 9 0 0 1 15.5-6.2L21 8" />
    <path d="M21 3v5h-5" />
    <path d="M21 12a9 9 0 0 1-15.5 6.2L3 16" />
    <path d="M3 21v-5h5" />
  </svg>
);

const SimIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 3h8l4 4v14H6z" />
    <rect x="9" y="11" width="6" height="6" rx="1" />
  </svg>
);

const TopupIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const RenameIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 3l4 4L7 21H3v-4z" />
  </svg>
);

const TrashIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18M8 6V4h8v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const RefundIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="1" x2="12" y2="23" />
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
  </svg>
);

const AgentIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

const DownloadIcon = (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12m0 0l-4-4m4 4l4-4" />
    <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
  </svg>
);

function defaultStatementRange() {
  const fmt = (d) => d.toISOString().slice(0, 10);
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: fmt(from), to: fmt(to) };
}

// Small icon + tooltip that expands into a date-range picker, then downloads
// the CSV via a plain navigation (server sends Content-Disposition: attachment).
function StatementDownload() {
  const [open, setOpen] = useState(false);
  const [range, setRange] = useState(defaultStatementRange);
  const today = useMemo(() => defaultStatementRange().to, []);

  function download() {
    const params = new URLSearchParams({ from: range.from, to: range.to });
    window.location.href = `/api/statement?${params.toString()}`;
    setOpen(false);
  }

  return (
    <div className="statement-download">
      <button
        type="button" className="icon-btn" title="Download statement" aria-label="Download statement"
        onClick={() => setOpen((v) => !v)}
      >
        {DownloadIcon}
      </button>
      {open && (
        <div className="statement-panel">
          <label>
            From
            <input type="date" value={range.from} max={range.to} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
          </label>
          <label>
            To
            <input type="date" value={range.to} min={range.from} max={today} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          </label>
          <button type="button" className="btn btn-small" onClick={download}>Download CSV</button>
        </div>
      )}
    </div>
  );
}

function orderContactUrl(o, type) {
  const p = new URLSearchParams();
  p.set('orderId', o.id);
  p.set('type', type);
  if (type === 'topup') {
    const da = orderDisplayAmount(o);
    const sym = da.currency === 'THB' ? '฿' : da.currency === 'USD' ? '$' : '';
    const amtStr = da.amount != null ? `${sym}${da.amount}${!sym ? ' ' + da.currency : ''}` : '';
    p.set('summary', `${o.phoneNumberInt || ''} — ${o.carrier || ''} ${amtStr}`);
  } else if (type === 'esim') {
    p.set('summary', o.name || o.slug || '');
  }
  p.set('status', o.status || '');
  p.set('date', o.createdAt || o.created_at || '');
  return `/contact?${p.toString()}`;
}

///////////////////////////////////////////////////////////////////////////////
// /login
///////////////////////////////////////////////////////////////////////////////

function useGoogleAuth(onSuccess, onError) {
  const [ready, setReady] = useState(false);
  const clientRef = React.useRef(null);

  useEffect(() => {
    const clientId = MT.googleClientId;
    if (!clientId) return;

    function init() {
      if (!window.google || !window.google.accounts) return;
      clientRef.current = window.google.accounts.oauth2.initCodeClient({
        client_id: clientId,
        scope: 'email profile',
        ux_mode: 'popup',
        callback: (response) => {
          if (response.error) { onError('Google sign-in failed: ' + response.error); return; }
          if (response.code) onSuccess(response.code);
        },
        // GIS reports a blocked or closed popup only here; without it the
        // button silently does nothing (#233).
        error_callback: (err) => {
          const type = (err && err.type) || 'unknown';
          if (posthog.__loaded) posthog.capture('login_error', { method: 'google', type });
          onError(type === 'popup_closed'
            ? 'The Google sign-in window was closed. Please try again, or sign in with email.'
            : 'The Google sign-in window could not open. Allow pop-ups for this site, or sign in with email.');
        },
      });
      setReady(true);
    }

    if (window.google && window.google.accounts) { init(); return; }
    // GIS script loaded via web.pug; wait for it
    const iv = setInterval(() => {
      if (window.google && window.google.accounts) { clearInterval(iv); init(); }
    }, 200);
    return () => clearInterval(iv);
  }, []);

  return { ready, requestCode: () => clientRef.current && clientRef.current.requestCode() };
}

function useFacebookAuth(onSuccess, onError) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const appId = MT.facebookAppId;
    if (!appId) return;

    window.fbAsyncInit = () => {
      window.FB.init({ appId, cookie: true, xfbml: false, version: 'v19.0' });
      setReady(true);
    };

    if (window.FB) { setReady(true); return; }
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.defer = true;
    document.body.appendChild(s);
  }, []);

  function login() {
    if (!window.FB) return;
    window.FB.login((res) => {
      if (res.status === 'connected' && res.authResponse) {
        onSuccess(res.authResponse.accessToken, res.authResponse.userID);
      } else {
        onError('Facebook sign-in was cancelled.');
      }
    }, { scope: 'public_profile,email' });
  }

  return { ready, login };
}

export function LoginPage({ me, currency, onCurrency }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // Email of an account that exists but has not clicked its confirmation link
  // yet (#287): shown with a Resend action instead of the bare server message.
  const [unconfirmed, setUnconfirmed] = useState(null);
  // Email of a web-checkout guest account that has no password (#312): the
  // server mailed a set-password link instead of refusing with "Incorrect
  // password".
  const [noPassword, setNoPassword] = useState(null);
  const turnstile = useTurnstile('turnstile-login');

  // Already signed in — nothing to do here
  useEffect(() => {
    if (me) window.location.replace(safeReturnTo());
  }, [me]);

  function socialDone() { window.location.href = safeReturnTo(); }
  function socialErr(msg) { setError(msg); setBusy(false); }

  const google = useGoogleAuth(
    async (code) => {
      setBusy(true); setError(null);
      try {
        await postJson('/api/auth/google', { code });
        socialDone();
      } catch (err) { socialErr(err.message || 'Google sign-in failed.'); }
    },
    socialErr,
  );

  const facebook = useFacebookAuth(
    async (token, userId) => {
      setBusy(true); setError(null);
      try {
        await postJson('/api/auth/facebook', { token, userId });
        socialDone();
      } catch (err) { socialErr(err.message || 'Facebook sign-in failed.'); }
    },
    socialErr,
  );

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setUnconfirmed(null);
    setNoPassword(null);
    setBusy(true);
    try {
      const turnstileToken = turnstile.token;
      const recaptcha = turnstileToken ? null : await recaptchaToken('login');
      const username = email.trim().toLowerCase();
      // returnTo rides on the set-password link a guest account gets (#312)
      const returnTo = safeReturnTo();
      await postJson('/api/auth/login', { username, password, turnstileToken, recaptcha, ...(returnTo !== '/' ? { returnTo } : {}) });
      window.location.href = safeReturnTo();
    } catch (err) {
      if (err.code === 'not_confirmed_email') {
        setUnconfirmed(email.trim().toLowerCase());
      } else if (err.code === 'no_password') {
        setNoPassword(email.trim().toLowerCase());
      } else {
        setError(err.message || 'Login failed — check the email and password.');
      }
      turnstile.reset();
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Log in</h1></section>
      <div className="checkout-wrap">
        <form className="cocard" onSubmit={submit}>
          <h3 className="coh" style={{ textAlign: 'center' }}>Welcome back</h3>

          <div className="social-buttons">
            <button type="button" className="btn-social btn-google" disabled={busy || !google.ready} onClick={() => google.requestCode()}>
              <svg className="social-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
            <button type="button" className="btn-social btn-facebook" disabled={busy || !facebook.ready} onClick={() => facebook.login()}>
              <svg className="social-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
              Continue with Facebook
            </button>
          </div>

          <div className="social-divider">
            <span className="social-divider-line" />
            <span className="social-divider-text">or</span>
            <span className="social-divider-line" />
          </div>

          <input
            className="coinput" type="email" value={email} autoFocus autoComplete="email"
            onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-label="Email"
          />
          <input
            className="coinput" type="password" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} placeholder="Password" aria-label="Password"
          />
          {error && <div className="notice payerr">{error}</div>}
          {unconfirmed && (
            <div className="notice">
              <div>Confirm your email first — we sent a link to <strong>{unconfirmed}</strong>. Click it to activate your account, then log in.</div>
              <ResendConfirmation email={unconfirmed} />
            </div>
          )}
          {noPassword && <SetPasswordSent email={noPassword} />}
          <div id="turnstile-login" style={{ margin: '10px 0' }} />
          <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
            {busy ? 'Signing in…' : 'Log in'}
          </button>
          <div className="acct-links">
            <a href={`/reset-password?returnTo=${encodeURIComponent(safeReturnTo())}${email.trim() ? `&email=${encodeURIComponent(email.trim())}` : ''}`}>Forgot password?</a>
            <a href={`/signup?returnTo=${encodeURIComponent(safeReturnTo())}`}>Create an account</a>
          </div>
        </form>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /reset-password
// Two modes: (1) no ?token → request reset email, (2) ?token=xxx → set new password
///////////////////////////////////////////////////////////////////////////////

export function ResetPasswordPage({ me, currency, onCurrency }) {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  // Support both ?token=xxx (new) and /reset-password/:token (legacy email links)
  const pathMatch = typeof window !== 'undefined' && /^\/reset-password\/([a-f0-9-]{36})$/i.exec(window.location.pathname);
  const token = params.get('token') || (pathMatch && pathMatch[1]) || null;
  const prefillEmail = params.get('email') || '';

  // Already signed in
  useEffect(() => { if (me) window.location.replace(safeReturnTo()); }, [me]);

  return token
    ? <ResetPasswordForm token={token} currency={currency} onCurrency={onCurrency} />
    : <RequestResetForm prefillEmail={prefillEmail} currency={currency} onCurrency={onCurrency} />;
}

function RequestResetForm({ prefillEmail, currency, onCurrency }) {
  const [email, setEmail] = useState(prefillEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      // Carried on the token so the emailed link — usually opened in another
      // browser — can send her back to the checkout she left (#308).
      const returnTo = safeReturnTo();
      await postJson('/api/auth/request-reset-password', {
        email: email.trim().toLowerCase(),
        ...(returnTo !== '/' ? { returnTo } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(err.message || 'Could not send reset email.');
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <Header me={null} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Reset Password</h1></section>
      <div className="checkout-wrap">
        <div className="cocard">
          {sent ? (
            <>
              <h3 className="coh" style={{ textAlign: 'center' }}>Check your email</h3>
              <p style={{ textAlign: 'center', margin: '16px 0' }}>
                We sent password reset instructions to <strong>{email}</strong>. The link works for one hour.
                {safeReturnTo() !== '/' && ' Once you set a new password we\'ll take you back to finish what you started.'}
              </p>
              <div className="acct-links" style={{ justifyContent: 'center' }}>
                <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`}>Back to log in</a>
              </div>
            </>
          ) : (
            <form onSubmit={submit}>
              <h3 className="coh" style={{ textAlign: 'center' }}>Forgot your password?</h3>
              <p style={{ textAlign: 'center', color: 'var(--muted)', margin: '0 0 16px', fontSize: 14 }}>
                Enter your email and we'll send you a link to reset your password.
              </p>
              <input
                className="coinput" type="email" value={email} autoFocus autoComplete="email"
                onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-label="Email"
              />
              {error && <div className="notice payerr">{error}</div>}
              <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
                {busy ? 'Sending…' : 'Send reset link'}
              </button>
              <div className="acct-links">
                <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`}>Back to log in</a>
              </div>
            </form>
          )}
        </div>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

function ResetPasswordForm({ token, currency, onCurrency }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dead, setDead] = useState(false); // link expired or already used: offer a fresh one

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (isCommonPassword(password)) { setError('That password is too common. Choose something less guessable.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError(null);
    setBusy(true);
    try {
      const d = await postJson('/api/auth/reset-password', { password, token });
      // Back to the page that asked for the reset (stored on the token, e.g.
      // the checkout she left) — this tab is often not the one she started in.
      window.location.href = safePath(d && d.returnTo) || safeReturnTo();
    } catch (err) {
      setError(err.message || 'Could not reset password.');
      setDead(['token_expired', 'token_already_used', 'invalid_token'].includes(err.code));
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <Header me={null} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Set New Password</h1></section>
      <div className="checkout-wrap">
        <form className="cocard" onSubmit={submit}>
          <h3 className="coh" style={{ textAlign: 'center' }}>Choose a new password</h3>
          <input
            className="coinput" type="password" value={password} autoFocus autoComplete="new-password"
            onChange={(e) => setPassword(e.target.value)} placeholder="New password" aria-label="New password"
          />
          <input
            className="coinput" type="password" value={confirm} autoComplete="new-password"
            onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" aria-label="Confirm password"
          />
          {error && <div className="notice payerr">{error}</div>}
          {dead && (
            <div className="acct-links" style={{ justifyContent: 'center' }}>
              <a href="/reset-password">Request a new link</a>
            </div>
          )}
          <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
            {busy ? 'Resetting…' : 'Reset password'}
          </button>
        </form>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /signup
///////////////////////////////////////////////////////////////////////////////

export function SignupPage({ me, currency, onCurrency }) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  // A web-checkout guest signing up again (#312): the account exists without a
  // password, the server mailed a set-password link, show that instead of
  // "already exists, try logging in" (which cannot work).
  const [guestExists, setGuestExists] = useState(null);
  // refer-a-friend: prefilled from the invite cookie (server exposes it as
  // __MT.refCode) or ?ref=; editable so a code from WhatsApp can be typed in.
  const [referralCode, setReferralCode] = useState(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('ref');
      return q || (window.__MT && window.__MT.refCode) || '';
    } catch (e) { return ''; }
  });
  const turnstile = useTurnstile('turnstile-signup');

  useEffect(() => { if (me) window.location.replace(safeReturnTo()); }, [me]);

  function socialDone() { window.location.href = safeReturnTo(); }
  function socialErr(msg) { setError(msg); setBusy(false); }

  const google = useGoogleAuth(
    async (code) => {
      setBusy(true); setError(null);
      try {
        await postJson('/api/auth/google', { code });
        socialDone();
      } catch (err) { socialErr(err.message || 'Google sign-in failed.'); }
    },
    socialErr,
  );

  const facebook = useFacebookAuth(
    async (token, userId) => {
      setBusy(true); setError(null);
      try {
        await postJson('/api/auth/facebook', { token, userId });
        socialDone();
      } catch (err) { socialErr(err.message || 'Facebook sign-in failed.'); }
    },
    socialErr,
  );

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError(null);
    setBusy(true);
    try {
      const turnstileToken = turnstile.token;
      const recaptcha = turnstileToken ? null : await recaptchaToken('signup');
      // Stored on the account and handed back by the confirmation link, so
      // the customer lands where she started (checkout) in any browser (#312).
      const returnTo = safeReturnTo();
      await postJson('/api/auth/signup', {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        referralCode: referralCode.trim() || undefined,
        turnstileToken,
        recaptcha,
        ...(returnTo !== '/' ? { returnTo } : {}),
      });
      setDone(true);
    } catch (err) {
      if (err.code === 'guest_account_exists') {
        setGuestExists(email.trim().toLowerCase());
        setBusy(false);
        return;
      }
      setError(err.message || 'Signup failed — please try again.');
      turnstile.reset();
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Create Account</h1></section>
      <div className="checkout-wrap">
        <div className="cocard">
          {guestExists ? (
            <>
              <h3 className="coh" style={{ textAlign: 'center' }}>You already have an account</h3>
              <p style={{ textAlign: 'center', margin: '16px 0' }}>
                It was created when you checked out as a guest, so it only needs a password.
              </p>
              <SetPasswordSent email={guestExists} />
              <div className="acct-links" style={{ justifyContent: 'center' }}>
                <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`} style={{ color: 'var(--muted)' }}>Already set a password? Log in</a>
              </div>
            </>
          ) : done ? (
            <>
              <h3 className="coh" style={{ textAlign: 'center' }}>Check your email</h3>
              <p style={{ textAlign: 'center', margin: '16px 0' }}>
                We sent a confirmation link to <strong>{email.trim().toLowerCase()}</strong>. Click the link to activate your account — you can't log in until then.
              </p>
              <ResendConfirmation email={email.trim().toLowerCase()} />
              <div className="acct-links" style={{ justifyContent: 'center' }}>
                <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`} style={{ color: 'var(--muted)' }}>Already confirmed? Log in</a>
              </div>
            </>
          ) : (
            <form onSubmit={submit}>
              <h3 className="coh" style={{ textAlign: 'center' }}>Sign up</h3>

              <div className="social-buttons">
                <button type="button" className="btn-social btn-google" disabled={busy || !google.ready} onClick={() => google.requestCode()}>
                  <svg className="social-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </button>
                <button type="button" className="btn-social btn-facebook" disabled={busy || !facebook.ready} onClick={() => facebook.login()}>
                  <svg className="social-icon" viewBox="0 0 24 24" width="20" height="20">
                    <path fill="#fff" d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Continue with Facebook
                </button>
              </div>

              <div className="social-divider">
                <span className="social-divider-line" />
                <span className="social-divider-text">or</span>
                <span className="social-divider-line" />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  className="coinput" type="text" value={firstName} autoFocus autoComplete="given-name"
                  onChange={(e) => setFirstName(e.target.value)} placeholder="First name" aria-label="First name"
                  style={{ flex: 1 }}
                />
                <input
                  className="coinput" type="text" value={lastName} autoComplete="family-name"
                  onChange={(e) => setLastName(e.target.value)} placeholder="Last name" aria-label="Last name"
                  style={{ flex: 1 }}
                />
              </div>
              <input
                className="coinput" type="email" value={email} autoComplete="email"
                onChange={(e) => setEmail(e.target.value)} placeholder="Email" aria-label="Email"
              />
              <input
                className="coinput" type="password" value={password} autoComplete="new-password"
                onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ characters)" aria-label="Password"
              />
              <input
                className="coinput" type="text" value={referralCode} autoComplete="off" autoCapitalize="none"
                onChange={(e) => setReferralCode(e.target.value)} placeholder="Referral code (optional)" aria-label="Referral code"
              />
              {referralCode && <div className="cohint" style={{ marginTop: -4 }}>Invited by a friend — your credit arrives after your first delivered order.</div>}
              {error && <div className="notice payerr">{error}</div>}
              <div id="turnstile-signup" style={{ margin: '10px 0' }} />
              <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
                {busy ? 'Creating account…' : 'Create account'}
              </button>
              <div className="acct-links">
                <a href={`/login?returnTo=${encodeURIComponent(safeReturnTo())}`}>Already have an account? Log in</a>
              </div>
            </form>
          )}
        </div>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /confirm-email/:token — verifies the email and logs the user in.
// #314: the link is often opened twice (reload, in-app webview + system
// browser, or clicked after a paid order already verified the account). The
// server answers 400 verified_already then; that is not a failure for the
// customer, so it gets its own "already confirmed" screen with a login path.
// #336: the POST waits for a click. Link checkers and crawlers render the
// page but don't click, so they no longer confirm (and sign in) before she
// does. A legacy id link verifies without signing in (signedIn: false).
///////////////////////////////////////////////////////////////////////////////

function ChoosePasswordForm({ userId, next }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (isCommonPassword(password)) { setError('That password is too common. Choose something less guessable.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setError(null);
    setBusy(true);
    try {
      // The confirmation just signed this browser in; the profile route sets
      // a password for the session's own account and drops the guest flag.
      await postJson(`/api/auth/profile/password/${userId}`, { n: password, r: confirm });
      window.location.href = next;
    } catch (err) {
      setError(err.message || 'Could not save the password.');
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} style={{ textAlign: 'left' }}>
      <input
        className="coinput" type="password" value={password} autoFocus autoComplete="new-password"
        onChange={(e) => setPassword(e.target.value)} placeholder="Password (6+ characters)" aria-label="Password"
      />
      <input
        className="coinput" type="password" value={confirm} autoComplete="new-password"
        onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" aria-label="Confirm password"
      />
      {error && <div className="notice payerr">{error}</div>}
      <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
        {busy ? 'Saving…' : 'Save password and continue'}
      </button>
      <div className="acct-links" style={{ justifyContent: 'center' }}>
        <a href={next} style={{ color: 'var(--muted)' }}>Skip for now</a>
      </div>
    </form>
  );
}

export function ConfirmEmailPage({ me, currency, onCurrency }) {
  const pathMatch = /^\/confirm-email\/([a-f0-9-]{36}|[a-f0-9]{64})$/i.exec(window.location.pathname);
  const token = pathMatch && pathMatch[1];
  // ready | loading | success | login | password | verified | error
  const [status, setStatus] = useState(token ? 'ready' : 'error');
  const [error, setError] = useState(token ? null : 'Invalid confirmation link.');
  const [next, setNext] = useState('/');
  const [userId, setUserId] = useState(null);
  const posted = React.useRef(false);

  function confirm() {
    // One POST per page: a double tap would come back verified_already.
    if (posted.current) return;
    posted.current = true;
    setStatus('loading');

    postJson('/api/auth/confirmation', { token })
      .then((d) => {
        // Back to where she was (checkout) when the account remembers it.
        const dest = safePath(d && d.returnTo) || safeReturnTo();
        setNext(dest);
        // Old-style link: confirmed, but this browser is not signed in.
        if (d && d.signedIn === false) { setStatus('login'); return; }
        // A web-checkout guest has no password: this link signed in only
        // this browser, so offer to choose one before leaving (#312).
        if (d && d.hasPassword === false && d.currentUser && d.currentUser.id) {
          setUserId(d.currentUser.id);
          setStatus('password');
          return;
        }
        setStatus('success');
        setTimeout(() => { window.location.href = dest; }, 2000);
      })
      .catch((err) => {
        if (err.code === 'verified_already') { setStatus('verified'); return; }
        setStatus('error');
        setError(err.message || 'Could not confirm your email.');
      });
  }

  const loginHref = `/login?returnTo=${encodeURIComponent(safeReturnTo())}`;

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Email Confirmation</h1></section>
      <div className="checkout-wrap">
        <div className="cocard" style={{ textAlign: 'center' }}>
          {status === 'ready' && (
            <>
              <h3 className="coh">Confirm your email</h3>
              <p style={{ margin: '16px 0' }}>Tap the button to confirm your email address and finish setting up your account.</p>
              <button type="button" className="btn block colarge" onClick={confirm} style={{ marginTop: 10 }}>Confirm my email</button>
            </>
          )}
          {status === 'loading' && (
            <>
              <h3 className="coh">Verifying your email…</h3>
              <p style={{ color: 'var(--muted)', margin: '16px 0' }}>Please wait a moment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <h3 className="coh">Email confirmed!</h3>
              <p style={{ margin: '16px 0' }}>
                {next === '/' ? 'Your account is active. Redirecting you to the homepage…' : 'Your account is active. Taking you back to where you left off…'}
              </p>
              <a href={next} className="btn block colarge" style={{ marginTop: 10 }}>{next === '/' ? 'Go to homepage' : 'Continue'}</a>
            </>
          )}
          {status === 'login' && (
            <>
              <h3 className="coh">Email confirmed!</h3>
              <p style={{ margin: '16px 0' }}>Your email is confirmed. Log in to continue.</p>
              <a href={`/login?returnTo=${encodeURIComponent(next)}`} className="btn block colarge" style={{ marginTop: 10 }}>Log in</a>
            </>
          )}
          {status === 'password' && (
            <>
              <h3 className="coh">Email confirmed!</h3>
              <p style={{ margin: '16px 0' }}>
                Choose a password so you can log in on any device. Your account was created at checkout and doesn't have one yet.
              </p>
              <ChoosePasswordForm userId={userId} next={next} />
            </>
          )}
          {status === 'verified' && (
            <>
              <h3 className="coh">Email already confirmed</h3>
              {me ? (
                <>
                  <p style={{ margin: '16px 0' }}>This email was confirmed earlier and you're signed in.</p>
                  <a href={safeReturnTo()} className="btn block colarge" style={{ marginTop: 10 }}>Continue</a>
                </>
              ) : (
                <>
                  <p style={{ margin: '16px 0' }}>This email was confirmed earlier. Log in to continue.</p>
                  <a href={loginHref} className="btn block colarge" style={{ marginTop: 10 }}>Log in</a>
                </>
              )}
            </>
          )}
          {status === 'error' && (
            <>
              <h3 className="coh">Confirmation failed</h3>
              <div className="notice payerr">{error}</div>
              <div className="acct-links" style={{ marginTop: 16, justifyContent: 'center' }}>
                <a href={loginHref}>Go to log in</a>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /confirm-email-change/:token — link sent to the NEW address (GitLab #105)
///////////////////////////////////////////////////////////////////////////////

export function ConfirmEmailChangePage({ me, currency, onCurrency }) {
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [error, setError] = useState(null);
  const [newEmail, setNewEmail] = useState(null);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const pathMatch = /^\/confirm-email-change\/([a-f0-9]{64})$/i.exec(window.location.pathname);
    const token = pathMatch && pathMatch[1];
    if (!token) { setStatus('error'); setError('Invalid confirmation link.'); return; }

    postJson('/api/auth/email-change/confirm', { token })
      .then((d) => {
        setNewEmail(d.email);
        setSignedIn(!!d.currentUser);
        setStatus('success');
      })
      .catch((err) => {
        setStatus('error');
        setError(err.message || 'Could not change your email.');
      });
  }, []);

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <section className="hero acct-hero"><h1 className="hero-title">Change Email</h1></section>
      <div className="checkout-wrap">
        <div className="cocard" style={{ textAlign: 'center' }}>
          {status === 'loading' && (
            <>
              <h3 className="coh">Confirming your new email…</h3>
              <p style={{ color: 'var(--muted)', margin: '16px 0' }}>Please wait a moment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <h3 className="coh">Email changed</h3>
              <p style={{ margin: '16px 0' }}>
                Your account email is now <strong>{newEmail}</strong>. For your security, other devices have been signed out.
              </p>
              {signedIn
                ? <a href="/settings" className="btn block colarge" style={{ marginTop: 10 }}>Go to settings</a>
                : <a href="/login" className="btn block colarge" style={{ marginTop: 10 }}>Log in</a>}
            </>
          )}
          {status === 'error' && (
            <>
              <h3 className="coh">Email not changed</h3>
              <div className="notice payerr">{error}</div>
              <div className="acct-links" style={{ marginTop: 16, justifyContent: 'center' }}>
                <a href="/settings">Go to settings</a>
              </div>
            </>
          )}
        </div>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /orders
///////////////////////////////////////////////////////////////////////////////
const PAGE = 20;

// Thai carriers ship local logos; everything else gets an initials chip.
const CARRIER_LOGOS = { ais: 'ais.png', dtac: 'dtac.png', truemove: 'truemove.png', true: 'truemove.png' };

function CarrierLogo({ order }) {
  const key = String(order.carrier || '').toLowerCase();
  const file = CARRIER_LOGOS[key];
  if (file) return <img className="carrier-logo" src={`${ST}/images/operators/${file}`} alt={order.carrier} loading="lazy" />;
  const label = (order.carrier || (order.dingData && order.dingData.sku) || order.method || '?').slice(0, 2).toUpperCase();
  return <span className="carrier-logo ph">{label}</span>;
}

function EsimActions({ order: o, onRefund }) {
  const [refunding, setRefunding] = useState(false);
  const code = o.slug || o.package_code;
  const delivered = o.status === 'paid' && o.purchase_status === 'success';

  async function handleRefund() {
    if (!window.confirm('Request a refund for this eSIM? This only works if the eSIM is unused.')) return;
    setRefunding(true);
    try {
      const r = await fetch(`/api/esims/refund/${o.id}`, { method: 'POST', credentials: 'same-origin' });
      const data = await r.json();
      if (!r.ok) {
        alert(data.message || data.error || 'Refund failed');
        return;
      }
      onRefund(o.id);
    } catch (e) {
      alert('Refund request failed — try again later.');
    } finally {
      setRefunding(false);
    }
  }

  return (
    <div className="ar-actions">
      <IconLink href={`/receipt/${o.id}`} title="View receipt">{ReceiptIcon}</IconLink>
      {o.install_url && <IconLink href={o.install_url} title="Install eSIM" external>{SimIcon}</IconLink>}
      {o.supports_topup && delivered && (
        <IconLink href={`/esim/topup/${o.id}`} title="Top up eSIM">{TopupIcon}</IconLink>
      )}
      {code && (
        <IconLink href={`/checkout?type=esim&code=${encodeURIComponent(code)}`} title="Order again">{RepeatIcon}</IconLink>
      )}
      {delivered && (
        <button
          type="button" className="icon-btn" title="Request refund (unused eSIM only)"
          disabled={refunding} onClick={handleRefund}
        >
          {RefundIcon}
        </button>
      )}
      <IconLink href={orderContactUrl(o, 'esim')} title="Get help">{AgentIcon}</IconLink>
    </div>
  );
}

export function OrdersPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/orders');
  const [orders, setOrders] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [esims, setEsims] = useState(null);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const searchTimer = React.useRef(null);

  function load(pg, phone) {
    setOrders(null);
    const params = new URLSearchParams({ limit: PAGE, offset: pg * PAGE, rels: '' });
    if (phone) params.set('phone', phone);
    return fetch(`/api/orders?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setOrders(d.orders || []);
        setTotal(d.meta ? d.meta.total : (d.orders || []).length);
      });
  }

  useEffect(() => {
    if (!me) return;
    load(0, '').catch(() => setError(true));
    fetch('/api/esims/orders')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setEsims((d && d.orders) || []))
      .catch(() => setEsims([]));
  }, [me]);

  function handleSearch(val) {
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setActiveSearch(val.trim());
      setPage(0);
      load(0, val.trim()).catch(() => setError(true));
    }, 400);
  }

  function goPage(pg) {
    setPage(pg);
    load(pg, activeSearch).catch(() => setError(true));
  }

  const totalPages = Math.ceil(total / PAGE);

  const rail = (
    <div className="railcard trust">
      <h4>Need help with an order?</h4>
      Click the <span style={{ display: 'inline-flex', verticalAlign: 'middle', margin: '0 2px' }}>{AgentIcon}</span> icon
      on any order to get help from our support agent.
    </div>
  );

  // Classify orders from /api/orders into topups vs other
  const isTopupOrder = (o) => !!(o.carrier || (o.dingData && o.dingData.sku));
  const topupOrders = useMemo(() => orders ? orders.filter(isTopupOrder) : null, [orders]);
  const otherOrders = useMemo(() => orders ? orders.filter((o) => !isTopupOrder(o)) : null, [orders]);

  const topupCount = topupOrders ? topupOrders.length : 0;
  const esimCount = esims ? esims.length : 0;
  const otherCount = otherOrders ? otherOrders.length : 0;
  const allCount = topupCount + esimCount + otherCount;

  // Default tab: category with most recent order (not "all")
  const [tab, setTab] = useState(null);
  useEffect(() => {
    if (tab !== null) return;
    if (orders == null || esims == null) return;
    const latest = (arr, key) => arr && arr.length ? new Date(arr[0][key]).getTime() : 0;
    const candidates = [
      { tab: 'topup', t: latest(topupOrders, 'createdAt') },
      { tab: 'esim', t: latest(esims, 'created_at') },
      { tab: 'other', t: latest(otherOrders, 'createdAt') },
    ].filter((c) => c.t > 0);
    if (!candidates.length) { setTab('topup'); return; }
    candidates.sort((a, b) => b.t - a.t);
    setTab(candidates[0].tab);
  }, [orders, esims, topupOrders, otherOrders, tab]);

  // Merged list for All tab
  const allItems = useMemo(() => {
    if (orders == null && esims == null) return null;
    const items = [];
    if (orders) orders.forEach((o) => items.push({ ...o, _type: isTopupOrder(o) ? 'topup' : 'other' }));
    if (esims) esims.forEach((o) => items.push({ ...o, _type: 'esim', createdAt: o.created_at }));
    items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    return items;
  }, [orders, esims]);

  function renderOrderRow(o) {
    return (
      <div key={o.id} className="acct-row static">
        <CarrierLogo order={o} />
        <div className="ar-main">
          <b>{o.phoneNumberInt || (o.dingData && o.dingData.sku) || 'Order'}</b>
          <span className="ar-sub">{fmtDate(o.createdAt)}</span>
          {o.retry_url && (
            // Money never taken (expired Xendit invoice, declined card): pay
            // the same product again, card first (#279)
            <a className="ar-retry" href={o.retry_url}>
              {o.method === 'xendit' ? 'Pay with card instead' : 'Try another payment method'}
            </a>
          )}
        </div>
        <div className="ar-side">
          {o.topupAmount != null && (() => {
            const da = orderDisplayAmount(o);
            const sym = da.currency === 'THB' ? '฿' : da.currency === 'USD' ? '$' : '';
            return <span className="ar-amount">{sym}{da.amount}{!sym ? ' ' + da.currency : ''}</span>;
          })()}
          <StatusChip status={o.status === 'refunded' ? 'REFUNDED' : (o.status === 'paid' && o.topup && o.topup.status === 'FAILED') ? 'TOPUP FAILED' : o.status} />
        </div>
        <div className="ar-actions">
          <IconLink href={`/receipt/${o.id}`} title="View receipt">{ReceiptIcon}</IconLink>
          {o.phoneNumberInt && (
            <IconLink href={`/recharge?phone=${encodeURIComponent(o.phoneNumberInt)}`} title="Order again">{RepeatIcon}</IconLink>
          )}
          <IconLink href={orderContactUrl(o, 'topup')} title="Get help">{AgentIcon}</IconLink>
        </div>
      </div>
    );
  }

  function renderEsimRow(o) {
    return (
      <div key={o.id} className="acct-row static">
        <span className="carrier-logo ph">eS</span>
        <div className="ar-main">
          <b>{o.name || o.slug || o.package_code}</b>
          <span className="ar-sub">{fmtDate(o.created_at || o.createdAt)}</span>
        </div>
        <div className="ar-side">
          {o.total_thb != null || o.price_usd != null ? (() => {
            const isUsd = o.method === 'balance_usd' || o.method === 'stripe-crypto' || o.method === 'stripe-esim';
            if (isUsd && o.price_usd != null) return <span className="ar-amount">${Number(o.price_usd).toFixed(2)}</span>;
            if (o.total_thb != null) return <span className="ar-amount">฿{Number(o.total_thb).toFixed(2)}</span>;
            return <span className="ar-amount">{fmtPrice(Number(o.price_usd))}</span>;
          })() : null}
          <StatusChip status={o.purchase_status || o.status} />
        </div>
        <EsimActions order={o} onRefund={(id) => {
          setEsims((cur) => cur.map((x) => x.id === id ? { ...x, status: 'refunded' } : x));
        }} />
      </div>
    );
  }

  function renderItem(o) {
    return o._type === 'esim' ? renderEsimRow(o) : renderOrderRow(o);
  }

  const loading = orders == null || esims == null || tab === null;

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="My orders" rail={rail}>
      {error && <div className="notice">Couldn't load your orders. Refresh to try again.</div>}

      {/* Filter tabs — always visible */}
      <div className="order-tabs">
        {[
          { key: 'all', label: 'All', count: allCount },
          { key: 'topup', label: 'Topups', count: topupCount },
          { key: 'esim', label: 'eSIMs', count: esimCount },
          { key: 'other', label: 'Other', count: otherCount },
        ].map((t) => (
          <button key={t.key} type="button" className={`order-tab${tab === t.key ? ' on' : ''}`} onClick={() => setTab(t.key)}>
            {t.label} ({t.count})
          </button>
        ))}
        <StatementDownload />
      </div>

      {/* Search (topup / all / other tabs) */}
      {(tab === 'topup' || tab === 'all' || tab === 'other') && (
        <div className="orders-search">
          <input type="search" className="coinput" placeholder="Search by phone number…" value={search} onChange={(e) => handleSearch(e.target.value)} />
        </div>
      )}

      {/* Loading skeleton */}
      {loading && <div className="acct-list">{[1, 2, 3, 4].map((i) => <div key={i} className="acct-row skeleton" />)}</div>}

      {/* All tab */}
      {!loading && tab === 'all' && (
        allItems && allItems.length > 0
          ? <div className="acct-list">{allItems.map(renderItem)}</div>
          : <div className="notice">No orders yet — <a href="/recharge">send your first topup</a></div>
      )}

      {/* Topups tab */}
      {!loading && tab === 'topup' && (
        topupOrders && topupOrders.length > 0
          ? <div className="acct-list">{topupOrders.map(renderOrderRow)}</div>
          : <div className="notice">{activeSearch ? 'No orders match that number.' : 'No topup orders yet — '}{!activeSearch && <a href="/recharge">send your first topup</a>}</div>
      )}

      {/* eSIMs tab */}
      {!loading && tab === 'esim' && (
        esims && esims.length > 0
          ? <div className="acct-list">{esims.map(renderEsimRow)}</div>
          : <div className="notice">No eSIM orders yet — <a href="/esim">browse eSIMs</a></div>
      )}

      {/* Other tab */}
      {!loading && tab === 'other' && (
        otherOrders && otherOrders.length > 0
          ? <div className="acct-list">{otherOrders.map(renderOrderRow)}</div>
          : <div className="notice">No other orders.</div>
      )}

      {/* Pagination */}
      {!loading && (tab === 'topup' || tab === 'all' || tab === 'other') && totalPages > 1 && (
        <div className="orders-pager">
          <button type="button" className="btn ghosted small" disabled={page === 0} onClick={() => goPage(page - 1)}>← Prev</button>
          <span className="pager-info">Page {page + 1} of {totalPages}</span>
          <button type="button" className="btn ghosted small" disabled={page >= totalPages - 1} onClick={() => goPage(page + 1)}>Next →</button>
        </div>
      )}
    </AccountShell>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /wallet
///////////////////////////////////////////////////////////////////////////////
export function WalletPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/wallet');
  const [data, setData] = useState(null);
  const [balances, setBalances] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!me) return;
    // THB history renders the page — don't block on the slow USD bridge call
    fetch('/api/orders/balance/history')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error())))
      .then((history) => setData(history))
      .catch(() => setError(true));
    // USD balance + transactions (can take 1-2s on Vercel cold start)
    fetch('/api/wallet/balances?transactions=1')
      .then((r) => (r.ok ? r.json() : null))
      .then((bal) => { if (bal) setBalances(bal); })
      .catch(() => {});
  }, [me]);

  const rail = (
    <div className="railcard trust">
      <h4>Why keep a balance?</h4>
      Balance payments skip card fees entirely and clear instantly — the
      fastest way to top up regularly.
    </div>
  );

  const usdAmount = (balances && balances.usd) || 0;
  const depositParam = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('deposit');
  const paymentId = typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('paymentId');
  const [depositStatus, setDepositStatus] = useState(
    (depositParam === 'pending' || depositParam === 'processing') ? 'checking' : null
  );

  // Complete 3DS deposit when returning from Omise bank auth
  useEffect(() => {
    if (depositParam !== 'processing' || !paymentId) return;
    let cancelled = false;
    fetch('/api/payment/omise/deposit-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ paymentId }),
    })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => {
        if (cancelled) return;
        if (!d) { setDepositStatus('failed'); return; }
        if (d.depositStatus === 'fulfilled') { setDepositStatus('success'); return; }
        if (d.depositStatus === 'failed') { setDepositStatus('failed'); return; }
        // Still pending — poll status a few times
        let attempts = 0;
        const poll = () => {
          fetch(`/api/payment/thai-bank/status?id=${encodeURIComponent(paymentId)}`)
            .then((r) => r.ok ? r.json() : null)
            .then((d2) => {
              if (cancelled) return;
              if (!d2) { setDepositStatus('failed'); return; }
              if (d2.status === 'fulfilled') { setDepositStatus('success'); return; }
              if (d2.status === 'failed') { setDepositStatus('failed'); return; }
              attempts++;
              if (attempts >= 6) { setDepositStatus('timeout'); return; }
              setTimeout(poll, 5000);
            })
            .catch(() => { if (!cancelled) setDepositStatus('failed'); });
        };
        setTimeout(poll, 3000);
      })
      .catch(() => { if (!cancelled) setDepositStatus('failed'); });
    return () => { cancelled = true; };
  }, [depositParam, paymentId]);

  // Poll deposit status when returning from Payment Links+
  useEffect(() => {
    if (depositParam !== 'pending' || !paymentId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = () => {
      fetch(`/api/payment/thai-bank/status?id=${encodeURIComponent(paymentId)}`)
        .then((r) => r.ok ? r.json() : null)
        .then((d) => {
          if (cancelled) return;
          if (!d) { setDepositStatus('failed'); return; }
          if (d.status === 'fulfilled') { setDepositStatus('success'); return; }
          if (d.status === 'failed') { setDepositStatus('failed'); return; }
          // Still pending/awaiting_payment — retry up to 6 times (30s total)
          attempts++;
          if (attempts >= 6) { setDepositStatus('timeout'); return; }
          setTimeout(poll, 5000);
        })
        .catch(() => { if (!cancelled) setDepositStatus('failed'); });
    };
    poll();
    return () => { cancelled = true; };
  }, [depositParam, paymentId]);

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Wallet" rail={rail}>
      {depositParam === 'success' && <div className="notice" style={{ background: 'rgba(46,204,113,.1)', color: '#27ae60', border: '1px solid rgba(46,204,113,.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>Deposit successful! Your balance has been updated.</div>}
      {depositParam === 'cancel' && <div className="notice bad">Deposit was cancelled.</div>}
      {depositStatus === 'checking' && <div className="notice" style={{ background: 'rgba(52,152,219,.1)', color: '#2980b9', border: '1px solid rgba(52,152,219,.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>Checking payment status...</div>}
      {depositStatus === 'success' && <div className="notice" style={{ background: 'rgba(46,204,113,.1)', color: '#27ae60', border: '1px solid rgba(46,204,113,.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>Deposit successful! Your balance has been updated.</div>}
      {depositStatus === 'failed' && <div className="notice bad">Payment was not completed. No charge was made.</div>}
      {depositStatus === 'timeout' && <div className="notice" style={{ background: 'rgba(243,156,18,.1)', color: '#e67e22', border: '1px solid rgba(243,156,18,.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>Payment is still processing. Your balance will be updated shortly.</div>}
      {error && <div className="notice">Couldn't load your balance. Refresh to try again.</div>}
      {!error && data == null && <div className="acct-list"><div className="acct-row skeleton" /></div>}
      {data != null && (
        <>
          <div className="wallet-cards">
            <div className="wallet-card">
              <div>
                <span className="wc-label">THB balance</span>
                <b className="wc-amount">{fmtBaht(data.balance || 0)}</b>
                <span className="wc-note">Thai mobile topup</span>
              </div>
              <a className="btn" style={{ padding: '11px 26px' }} href="/wallet/deposit">Deposit</a>
            </div>
            <div className="wallet-card">
              <div>
                <span className="wc-label">USD balance</span>
                <b className="wc-amount">{balances ? `$${Number(usdAmount).toFixed(2)}` : '$\u2014'}</b>
                <span className="wc-note">eSIM, airtime, gifts</span>
              </div>
              <a className="btn btn-alt" style={{ padding: '11px 26px' }} href="/wallet/deposit/usd">Add USD</a>
            </div>
          </div>

          {data.isTrusted && <span className="tag good" style={{ display: 'inline-block', marginTop: 10 }}>Trusted account</span>}

          <h3 className="acct-h3" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            Transaction history
            <StatementDownload />
          </h3>
          {(() => {
            // THB transactions from RethinkDB
            const thbTxs = (data.transactions || []).map((tx) => {
              const isAdd = tx.type === 'add';
              const method = tx.method || '';
              let label;
              if (isAdd) {
                label = method === 'refund' ? 'Refund' : 'Deposit';
              } else {
                label = 'Payment';
              }
              if (tx.details) label += ` · ${tx.details}`;
              else if (method && method !== 'refund') label += ` · ${method}`;
              return {
                id: tx.id, amount: tx.amount, currency: 'THB', isAdd, label,
                orderId: tx.orderId, date: new Date(tx.createdAt),
              };
            });
            // USD transactions from Neon (via bridge)
            const usdTxs = ((balances && balances.usdTransactions) || []).map((tx) => {
              const isAdd = tx.delta > 0;
              const reason = tx.reason || '';
              let label = isAdd ? 'Deposit' : 'Payment';
              if (reason === 'refund' || reason === 'order_refund') label = 'Refund';
              else if (reason === 'deposit' || reason === 'usdc_deposit') label = 'Deposit';
              else if (reason === 'conversion') label = 'Conversion';
              else if (reason) label += ` · ${reason}`;
              return {
                id: tx.id, amount: Math.abs(tx.delta), currency: 'USD', isAdd, label,
                orderId: tx.orderId || null, date: new Date(tx.createdAt),
              };
            });
            const all = [...thbTxs, ...usdTxs].sort((a, b) => b.date - a.date).slice(0, 30);
            if (all.length === 0) return <div className="notice">No transactions yet.</div>;
            return (
              <div className="acct-list">
                {all.map((tx) => (
                  <div key={tx.id} className="acct-row static">
                    <div className="ar-main">
                      <b style={{ color: tx.isAdd ? 'var(--success, #43AC6A)' : 'inherit' }}>
                        {tx.isAdd ? '+' : '−'}{tx.currency === 'USD' ? `$${tx.amount.toFixed(2)}` : fmtBaht(tx.amount)}
                      </b>
                      <span className="ar-sub">{tx.label}</span>
                      <span className="ar-sub">{fmtDate(tx.date)}</span>
                    </div>
                    {tx.orderId && (
                      <div className="ar-side">
                        <a href={`/receipt/${tx.orderId}`} style={{ fontSize: 12, color: 'var(--teal-deep)' }}>
                          {'ORD-' + tx.orderId.replace(/-/g, '').slice(0, 8).toUpperCase()}
                        </a>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </>
      )}
    </AccountShell>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /wallet/deposit — THB deposit via card (Omise or Stripe Checkout)
///////////////////////////////////////////////////////////////////////////////
const THB_AMOUNTS = [1000, 2000, 3000, 4000, 5000];
const roundMoney = (n) => Math.ceil(n * 100) / 100;

function getOmiseToken(card) {
  return new Promise((resolve, reject) => {
    if (!window.Omise) return reject(new Error('Card check failed. Disable script blockers.'));
    window.Omise.setPublicKey(MT.omisePublicKey);
    window.Omise.createToken('card', card, (status, token) => {
      if (status === 200 && token && token.id) resolve(token);
      else reject(token);
    });
  });
}

const DEPOSIT_THB_METHODS = [
  { code: 'bank', label: 'Thai Bank / Wallet', logos: ['kbank.png', 'scb.png', 'truemoney.svg', 'linepay.svg'] },
  { code: 'card', label: 'Card', logos: ['visa.png', 'master.png', 'jcb.png'] },
  { code: 'hosted', label: 'Card (hosted page)', logos: ['visa.png', 'master.png'] },
];

// Same tile styling as the checkout method picker (.methods / .method).
function DepositMethodPicker({ methods, method, onPick }) {
  return (
    <div className="methods dep-methods">
      {methods.map((m) => (
        <button type="button" key={m.code} className={`method ${method === m.code ? 'on' : ''}`}
          onClick={() => onPick(m.code)} aria-pressed={method === m.code}>
          <span className="mlogos">{m.logos.map((l) => <img key={l} src={`${ST}/images/payment-icons/${l}`} alt="" />)}</span>
          <span className="mname">{m.label}</span>
        </button>
      ))}
    </div>
  );
}

export function DepositTHBPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/wallet/deposit');
  const [amount, setAmount] = useState(1000);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [method, setMethod] = useState('bank');
  // Card form fields (non-trusted)
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardMonth, setCardMonth] = useState('');
  const [cardYear, setCardYear] = useState('');
  const [cardCvc, setCardCvc] = useState('');

  const isTrusted = me && me.isTrusted;

  // Fee calculation for card method
  const feeKey = isTrusted ? 'stripe' : 'omise';
  const fees = (typeof window !== 'undefined' && window.__MT_FEES) || {};
  // Fallback to known defaults if config not available
  const feePercent = (fees[feeKey] && (fees[feeKey].deposit_percent || fees[feeKey].percent)) || (isTrusted ? 0.055 : 0.04);
  const feeFixed = (fees[feeKey] && (fees[feeKey].deposit_fixed || fees[feeKey].fixed)) || (isTrusted ? 11 : 0);
  const cardFee = (me && me.disableCCFees) ? 0 : roundMoney(amount * feePercent + feeFixed);
  const cardTotal = roundMoney(amount + cardFee);

  // Thai Bank fee: flat 10 THB
  const bankFee = 10;
  const bankTotal = roundMoney(amount + bankFee);

  // Trusted users: Stripe Checkout redirect
  const handleStripeDeposit = () => {
    if (busy) return;
    setBusy(true); setError(null);
    fetch('/api/payment/stripe-checkout/deposit', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.url) { window.location = data.url; return; }
        const msg = data.message || (data.error && (typeof data.error === 'string' ? data.error : data.error.message)) || 'Could not start checkout';
        throw new Error(msg);
      })
      .catch((err) => { setError(err.message); setBusy(false); });
  };

  // Non-trusted: Omise card tokenisation + charge
  const handleOmiseDeposit = (e) => {
    e.preventDefault();
    if (busy) return;
    if (!cardName || !cardNumber || !cardMonth || !cardYear || !cardCvc) {
      setError('Please fill in all card details'); return;
    }
    setBusy(true); setError(null);
    getOmiseToken({
      name: cardName, number: cardNumber,
      expiration_month: cardMonth, expiration_year: cardYear.length === 2 ? '20' + cardYear : cardYear,
      security_code: cardCvc,
    })
      .then((token) =>
        fetch('/api/payment/omise/deposit', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          // Same shape as checkout: omise.deposit saves the card by token and charges it by id (#274)
          body: JSON.stringify({ amount, cardToken: token.id, cardId: token.card && token.card.id }),
        }).then((r) => r.json())
      )
      .then((data) => {
        if (data.url) { window.location = data.url; return; } // 3DS redirect
        if (data.status === 'ok') { window.location = '/wallet?deposit=success'; return; }
        throw new Error(data.message || 'Deposit failed');
      })
      .catch((err) => { setError(err.message || 'Card verification failed'); setBusy(false); });
  };

  // Thai Bank: Payment Links+ redirect
  const handleBankDeposit = () => {
    if (busy) return;
    setBusy(true); setError(null);
    fetch('/api/payment/thai-bank/deposit', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.payment_url) { window.location = data.payment_url; return; }
        const msg = data.message || (data.error && (typeof data.error === 'string' ? data.error : data.error.message)) || 'Could not start payment';
        throw new Error(msg);
      })
      .catch((err) => { setError(err.message); setBusy(false); });
  };

  // Card via hosted checkout (Payment Links+ template 758)
  const handleCardLinkDeposit = () => {
    if (busy) return;
    setBusy(true); setError(null);
    fetch('/api/payment/card-link/deposit', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.payment_url) { window.location = data.payment_url; return; }
        const msg = data.message || (data.error && (typeof data.error === 'string' ? data.error : data.error.message)) || 'Could not start payment';
        throw new Error(msg);
      })
      .catch((err) => { setError(err.message); setBusy(false); });
  };

  const rail = (
    <div className="railcard trust">
      <h4>THB balance</h4>
      Use your Thai Baht balance for topup or to convert to USD for eSIMs and other topup items.
    </div>
  );

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Deposit THB" rail={rail}>
      <a href="/wallet" className="dep-back">&larr; Back to Wallet</a>

      <h3 className="acct-h3">Select amount</h3>
      <div className="dep-amounts">
        {THB_AMOUNTS.map((a) => (
          <button key={a} type="button" className={'dep-amt' + (a === amount ? ' sel' : '')} onClick={() => setAmount(a)}>
            ฿{a.toLocaleString()}
          </button>
        ))}
      </div>

      <h3 className="acct-h3">Payment method</h3>
      <DepositMethodPicker
        methods={DEPOSIT_THB_METHODS.filter((m) => !(m.code === 'hosted' && isTrusted))}
        method={method}
        onPick={(m) => { setMethod(m); setError(null); }}
      />

      {error && <div className="notice bad">{error}</div>}

      {method === 'bank' && <div className="dep-method-group">
        <h3 className="acct-h3">Thai Bank Transfer</h3>
        <div className="dep-summary">
          <div className="dep-row"><span>Deposit</span><span>฿{amount.toLocaleString()}</span></div>
          <div className="dep-row"><span>Processing fee</span><span>฿{bankFee.toFixed(2)}</span></div>
          <div className="dep-row dep-total"><span>Total</span><span>฿{bankTotal.toFixed(2)}</span></div>
        </div>
        <button className="btn dep-btn" disabled={busy} onClick={handleBankDeposit}>
          {busy ? 'Redirecting...' : `Pay ฿${bankTotal.toFixed(2)} via Thai Bank`}
        </button>
      </div>}

      {method === 'card' && <div className="dep-method-group">
        <h3 className="acct-h3">Credit / Debit Card</h3>
        <div className="dep-summary">
          <div className="dep-row"><span>Deposit</span><span>฿{amount.toLocaleString()}</span></div>
          <div className="dep-row"><span>Processing fee</span><span>฿{cardFee.toFixed(2)}</span></div>
          <div className="dep-row dep-total"><span>Total</span><span>฿{cardTotal.toFixed(2)}</span></div>
        </div>

        {isTrusted ? (
          <button className="btn dep-btn" disabled={busy} onClick={handleStripeDeposit}>
            {busy ? 'Redirecting...' : `Pay ฿${cardTotal.toFixed(2)} with Card`}
          </button>
        ) : (
          <form onSubmit={handleOmiseDeposit} className="dep-form">
            <label>Card number</label>
            <input type="text" required autoComplete="cc-number" placeholder="4242 4242 4242 4242"
              value={cardNumber} onChange={(e) => setCardNumber(e.target.value)} />
            <label>Name on card</label>
            <input type="text" required autoComplete="cc-name" placeholder="Full name"
              value={cardName} onChange={(e) => setCardName(e.target.value)} />
            <div className="dep-row-inputs">
              <div>
                <label>Expiry</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" required autoComplete="cc-exp-month" placeholder="MM" maxLength="2" style={{ width: 60 }}
                    value={cardMonth} onChange={(e) => setCardMonth(e.target.value)} />
                  <input type="text" required autoComplete="cc-exp-year" placeholder="YY" maxLength="2" style={{ width: 60 }}
                    value={cardYear} onChange={(e) => setCardYear(e.target.value)} />
                </div>
              </div>
              <div>
                <label>CVC</label>
                <input type="text" required autoComplete="cc-csc" placeholder="123" maxLength="4" style={{ width: 80 }}
                  value={cardCvc} onChange={(e) => setCardCvc(e.target.value)} />
              </div>
            </div>
            <button type="submit" className="btn dep-btn" disabled={busy}>
              {busy ? 'Processing...' : `Pay ฿${cardTotal.toFixed(2)}`}
            </button>
          </form>
        )}
      </div>}

      {method === 'hosted' && !isTrusted && (
        <div className="dep-method-group">
          <h3 className="acct-h3">Card via Hosted Checkout</h3>
          <p style={{ fontSize: 13, color: '#666', margin: '0 0 10px' }}>
            Pay on a secure hosted page. Use this if on-site card payment is declined.
          </p>
          <div className="dep-summary">
            <div className="dep-row"><span>Deposit</span><span>฿{amount.toLocaleString()}</span></div>
            <div className="dep-row"><span>Processing fee</span><span>฿{cardFee.toFixed(2)}</span></div>
            <div className="dep-row dep-total"><span>Total</span><span>฿{cardTotal.toFixed(2)}</span></div>
          </div>
          <button className="btn dep-btn" disabled={busy} onClick={handleCardLinkDeposit}>
            {busy ? 'Redirecting...' : `Pay ฿${cardTotal.toFixed(2)} via Hosted Checkout`}
          </button>
        </div>
      )}
    </AccountShell>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /wallet/deposit/usd — USD deposit via USDC
///////////////////////////////////////////////////////////////////////////////
export function DepositUSDPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/wallet/deposit/usd');
  const [amount, setAmount] = useState('25');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const parsedAmount = parseFloat(amount) || 0;
  const valid = parsedAmount >= 5 && parsedAmount <= 500;

  const handleDeposit = () => {
    if (busy || !valid) return;
    setBusy(true); setError(null);
    fetch('/api/wallet/deposit-usd', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount: parsedAmount }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, data: d })))
      .then(({ ok, data }) => {
        if (ok && data.checkout_url) { window.location = data.checkout_url; return; }
        const msg = data.message || (data.error && (typeof data.error === 'string' ? data.error : data.error.message)) || 'Could not start checkout';
        throw new Error(msg);
      })
      .catch((err) => { setError(err.message); setBusy(false); });
  };

  const rail = (
    <div className="railcard trust">
      <h4>USD balance</h4>
      Your USD balance covers eSIM data plans, international airtime, and gift cards.
      Pay with USDC stablecoin.
    </div>
  );

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Add USD" rail={rail}>
      <a href="/wallet" className="dep-back">&larr; Back to Wallet</a>

      <h3 className="acct-h3">Enter amount (USD)</h3>
      <div className="dep-usd-row">
        {[10, 20, 50].map((a) => (
          <button key={a} type="button" className={'dep-amt' + (amount === String(a) ? ' sel' : '')}
            onClick={() => { setAmount(String(a)); setError(null); }}>
            ${a}
          </button>
        ))}
        <div className="dep-amount-input">
          <span className="dep-currency">$</span>
          <input
            type="number" min="5" max="500" step="0.01"
            placeholder="25.00" autoFocus
            value={amount}
            onChange={(e) => { setAmount(e.target.value); setError(null); }}
          />
        </div>
      </div>
      <p className="dep-hint">Min $5 &middot; Max $500 &middot; Pay with USDC</p>
      <div className="paylogos">
        {['ethereum.svg', 'arbitrum.svg', 'base.svg'].map((l) => <img key={l} src={`${ST}/images/payment-icons/${l}`} alt="" />)}
      </div>

      {error && <div className="notice bad">{error}</div>}

      <button className="btn dep-btn dep-btn-usd" disabled={busy || !valid} onClick={handleDeposit}>
        {busy ? 'Redirecting...' : valid ? `Add $${parsedAmount.toFixed(2)} USD · Pay with USDC` : 'Enter an amount'}
      </button>
    </AccountShell>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /numbers
///////////////////////////////////////////////////////////////////////////////

// Match an E.164 phone number to a country ISO using dial prefix data.
// Sorts by longest prefix first so +1868 (Trinidad) beats +1 (US).
function phoneToCountry(phone, dialMap) {
  if (!phone || !dialMap.length) return null;
  const digits = String(phone).replace(/\D/g, '');
  for (const { dial, iso } of dialMap) {
    if (digits.startsWith(dial)) return iso;
  }
  return null;
}

export function NumbersPage({ me, currency, onCurrency }) {
  useRequireAuth(me, '/numbers');
  const [numbers, setNumbers] = useState(null);
  const [error, setError] = useState(false);
  const [dialMap, setDialMap] = useState([]);
  const [carriers, setCarriers] = useState({});

  // Fetch countries for dial-prefix → ISO mapping
  useEffect(() => {
    fetch('/api/topup/countries')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d || d.status !== 'ok' || !Array.isArray(d.data)) return;
        const map = d.data
          .map((c) => ({
            iso: c.CountryIso,
            dial: (c.InternationalDialingInformation &&
                   c.InternationalDialingInformation[0] &&
                   c.InternationalDialingInformation[0].Prefix) || null,
          }))
          .filter((c) => c.dial && /^[A-Z]{2}$/.test(c.iso || ''));
        // Longest prefix first so +1868 beats +1
        map.sort((a, b) => b.dial.length - a.dial.length);
        setDialMap(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!me) return;
    fetch('/api/phonenumbers')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setNumbers((d.phoneNumbers || []).filter((n) => !n.isRemoved)))
      .catch(() => setError(true));
  }, [me]);

  // Background carrier lookup once we have numbers
  useEffect(() => {
    if (!numbers) return;
    numbers.forEach((n) => {
      const phone = n.number;
      if (!phone || carriers[phone]) return;
      fetch(`/api/carrier/lookup?phone=${encodeURIComponent(phone)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => {
          if (d && d.status === 'ok' && d.carrier) {
            setCarriers((prev) => ({ ...prev, [phone]: d.carrier }));
          }
        })
        .catch(() => {});
    });
  }, [numbers]);

  function topupUrl(n) {
    const phone = n.number || '';
    const iso = phoneToCountry(phone, dialMap);
    let url = `/recharge?phone=${encodeURIComponent(phone)}`;
    if (iso) url += `&country=${iso}`;
    return url;
  }

  async function rename(n) {
    const name = window.prompt('Name for this number', n.name || '');
    if (name == null) return;
    try {
      await postJson(`/api/phonenumbers/${n.id}/updateName`, { name });
      setNumbers((cur) => cur.map((x) => (x.id === n.id ? { ...x, name } : x)));
    } catch (e) { /* keep old name */ }
  }

  async function remove(n) {
    if (!window.confirm(`Remove ${n.number || n.name || 'this number'} from your saved numbers?`)) return;
    try {
      await postJson(`/api/phonenumbers/${n.id}/deleteNumber`, {});
      setNumbers((cur) => cur.filter((x) => x.id !== n.id));
    } catch (e) { /* leave it */ }
  }

  const rail = (
    <div className="railcard trust">
      <h4>Saved numbers</h4>
      Every number you top up is saved here automatically — name them to keep
      family and friends straight, and reorder in two taps.
    </div>
  );

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="My numbers" rail={rail}>
      {error && <div className="notice">Couldn't load your numbers. Refresh to try again.</div>}
      {!error && numbers == null && <div className="acct-list">{[1, 2, 3].map((i) => <div key={i} className="acct-row skeleton" />)}</div>}
      {numbers != null && numbers.length === 0 && (
        <div className="notice">No saved numbers yet — they appear here after your first topup.</div>
      )}
      {numbers != null && numbers.length > 0 && (
        <div className="acct-list">
          {numbers.map((n) => (
            <div key={n.id} className="acct-row static">
              <div className="ar-main">
                <b>{n.name || n.number}</b>
                {n.name && <span className="ar-sub">{n.number}</span>}
                {carriers[n.number] && (
                  <span className="ar-sub" style={{ fontSize: 12, color: 'var(--muted)' }}>{carriers[n.number]}</span>
                )}
              </div>
              <div className="ar-actions">
                <IconLink href={topupUrl(n)} title="Topup">{RepeatIcon}</IconLink>
                <button type="button" className="icon-btn" title="Rename" aria-label="Rename" onClick={() => rename(n)}>{RenameIcon}</button>
                <button type="button" className="icon-btn" title="Remove" aria-label="Remove" onClick={() => remove(n)}>{TrashIcon}</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </AccountShell>
  );
}


///////////////////////////////////////////////////////////////////////////////
// /esim/topup/:id — pick a topup package for a delivered eSIM
///////////////////////////////////////////////////////////////////////////////
const GB = 1073741824;
function dataLabel(bytes) {
  if (!bytes) return '';
  return bytes >= GB ? `${(bytes / GB).toFixed(bytes % GB === 0 ? 0 : 1)} GB` : `${Math.round(bytes / 1048576)} MB`;
}

function esimStatusBadge(smdp, esimSt) {
  if (smdp === 'ENABLED' && esimSt === 'IN_USE') return { icon: '●', color: '#4caf50', label: 'Active' };
  if (smdp === 'ENABLED' && esimSt === 'GOT_RESOURCE') return { icon: '○', color: '#ff9800', label: 'Ready' };
  if (esimSt === 'USED_UP') return { icon: '✕', color: '#f44336', label: 'Data used up' };
  if (smdp === 'DISABLED') return { icon: '⏸', color: '#9e9e9e', label: 'Disabled' };
  if (smdp === 'DELETED') return { icon: '✕', color: '#9e9e9e', label: 'Deleted' };
  return { icon: '◷', color: '#fff', label: smdp || esimSt || 'Unknown' };
}

function daysRemaining(expiredTime) {
  if (!expiredTime) return null;
  const diff = new Date(expiredTime).getTime() - Date.now();
  if (diff <= 0) return 0;
  return Math.ceil(diff / 86400000);
}

export function EsimTopupPage({ me, currency, onCurrency }) {
  const orderId = window.location.pathname.split('/')[3] || '';
  useRequireAuth(me, `/esim/topup/${orderId}`);
  const [order, setOrder] = useState(null);
  const [packages, setPackages] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!me || !orderId) return;
    fetch(`/api/esims/order/${encodeURIComponent(orderId)}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Order not found'))))
      .then(setOrder)
      .catch((e) => setError(e.message));
  }, [me, orderId]);

  useEffect(() => {
    if (!me || !orderId) return;
    fetch(`/api/esims/order/${encodeURIComponent(orderId)}/topup-packages`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not load topup packages'))))
      .then((d) => setPackages(d.packages || []))
      .catch((e) => setError(e.message));
  }, [me, orderId]);

  useEffect(() => {
    if (!me || !orderId) return;
    fetch(`/api/esims/order/${encodeURIComponent(orderId)}/status`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setStatus(d); setStatusLoaded(true); })
      .catch(() => setStatusLoaded(true));
  }, [me, orderId]);

  const hasData = status && status.data_total_bytes > 0;
  const usedBytes = status ? (status.data_used_bytes || 0) : 0;
  const totalBytes = status ? (status.data_total_bytes || 0) : 0;
  const remainingBytes = totalBytes - usedBytes;
  const usedPct = hasData ? Math.min(100, Math.round((usedBytes / totalBytes) * 100)) : 0;
  const days = status ? daysRemaining(status.expired_time) : null;
  const badge = status ? esimStatusBadge(status.smdp_status, status.esim_status) : null;

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Top up eSIM"
      hideSupportCard
    >
      {error && <div className="notice">{error}</div>}

      {/* Status card */}
      {order && (
        <div style={{
          background: 'linear-gradient(135deg, #3bb2b8 0%, #2a8f94 100%)',
          borderRadius: 12, padding: '20px 24px', color: '#fff', marginBottom: 24,
          maxWidth: 520,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{order.name}</div>
            {badge && (
              <span style={{
                fontSize: 11, background: 'rgba(255,255,255,0.2)', borderRadius: 10,
                padding: '2px 10px', display: 'inline-flex', alignItems: 'center', gap: 4,
              }}>
                <span style={{ color: badge.color }}>{badge.icon}</span> {badge.label}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 16 }}>
            Add additional data and extend validity.
          </div>

          {hasData ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 700 }}>{dataLabel(remainingBytes)}</span>
                <span style={{ fontSize: 13, opacity: 0.8 }}>of {dataLabel(totalBytes)} remaining</span>
              </div>
              <div style={{
                background: 'rgba(255,255,255,0.25)', borderRadius: 6, height: 10, overflow: 'hidden',
              }}>
                <div style={{
                  width: `${100 - usedPct}%`, height: '100%', borderRadius: 6,
                  background: usedPct > 90 ? '#f44336' : '#fff',
                  transition: 'width 0.4s ease',
                }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 11, opacity: 0.7 }}>
                <span>{dataLabel(usedBytes)} used ({usedPct}%)</span>
                {days != null && (
                  <span>{days > 0 ? `${days} day${days !== 1 ? 's' : ''} left` : 'Expired'}</span>
                )}
              </div>
            </>
          ) : !statusLoaded ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading status...</div>
          ) : (
            <div style={{ fontSize: 13, opacity: 0.8 }}>
              Usage data not yet available — eSIM may not be activated.
            </div>
          )}
        </div>
      )}

      {/* Loading skeleton */}
      {!error && packages == null && (
        <div className="acct-list">{[1, 2, 3].map((i) => <div key={i} className="acct-row skeleton" />)}</div>
      )}
      {packages != null && packages.length === 0 && (
        <div className="notice">No topup packages available for this eSIM.</div>
      )}
      {packages != null && packages.length > 0 && (
        <>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 12px', color: '#333' }}>Top-up options</h3>
          <div className="acct-list">
            {packages.map((p) => (
              <a key={p.package_code} className="acct-row"
                href={`/checkout?type=esim&code=${encodeURIComponent(p.slug || p.package_code)}`}
              >
                <span className="carrier-logo ph">eS</span>
                <div className="ar-main">
                  <b>{p.name}</b>
                  <span className="ar-sub">
                    {dataLabel(p.data_bytes)}
                    {p.duration ? ` · ${p.duration} ${p.duration_unit || 'days'}` : ''}
                  </span>
                </div>
                <div className="ar-side">
                  <span className="ar-amount">{fmtPrice(p.price)}</span>
                </div>
              </a>
            ))}
          </div>
        </>
      )}
      <p style={{ marginTop: 16, fontSize: 13 }}>
        <a href="/orders" style={{ color: '#888' }}>Back to orders</a>
      </p>
    </AccountShell>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /receipt/:id — public retail receipt, replacing the partners-hosted page
// (that subdomain is never shown to retail users). Follows the established
// receipt pattern: logos, PAYMENT DETAILS records, carrier branding.
///////////////////////////////////////////////////////////////////////////////
const OPERATOR_NAMES = { ais: 'AIS Thailand', dtac: 'DTAC Thailand', truemove: 'TrueMove H', true: 'TrueMove H' };

const METHOD_VIEW = {
  promptpay: { label: 'PromptPay', logos: ['promptpay.png'] },
  omise: { label: 'Credit / debit card', logos: ['visa.png', 'master.png', 'jcb.png'] },
  'omise-tt': { label: 'Credit / debit card', logos: ['visa.png', 'master.png', 'jcb.png'] },
  cards: { label: 'Credit / debit card', logos: ['visa.png', 'master.png'] },
  card: { label: 'Credit / debit card', logos: ['visa.png', 'master.png'] },
  stripe: { label: 'Card (Stripe)', logos: ['visa.png', 'master.png'] },
  'stripe-checkout': { label: 'Card (Stripe)', logos: ['visa.png', 'master.png'] },
  'stripe-esim': { label: 'Card (Stripe)', logos: ['visa.png', 'master.png'] },
  'stripe-crypto': { label: 'USDC (Stripe)', logos: [] },
  alipay: { label: 'Alipay', logos: ['alipay.png'] },
  paypal: { label: 'PayPal', logos: ['paypal.png'] },
  balance: { label: 'THB Balance', logos: [] },
  balance_usd: { label: 'USD Wallet', logos: [] },
  xendit: { label: 'Xendit', logos: ['philippine-peso.svg'] },
};

function ReceiptRecord({ name, children }) {
  return (
    <div className="record">
      <div className="record-name">{name}</div>
      <div className="record-value">{children}</div>
    </div>
  );
}

export function ReceiptPage({ me, currency, onCurrency }) {
  const id = window.location.pathname.split('/')[2] || '';
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`/api/receipt/${encodeURIComponent(id)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch(() => setError(true));
  }, [id]);

  const carrierKey = data ? String(data.carrier || '').toLowerCase() : '';
  const method = (data && METHOD_VIEW[String(data.method || '').toLowerCase()]) || { label: (data && data.method) || '—', logos: [] };

  const isEsim = data && data.type === 'esim';

  return (
    <div className="page">
      <Header me={me} currency={currency} onCurrency={onCurrency} />
      <div className="checkout-wrap receipt-wrap">
        <h1 className="receipt-title">Receipt</h1>

        <div className="receipt-logos">
          <img src={`${ST}/images/2/logo_x2.png`} height="28" alt="MobileTopup.com" />
          {carrierKey === 'ais' && (
            <img src={`${ST}/images/2/mpay.png`} width="70" height="70" alt="Operated by mPay Station" />
          )}
        </div>

        {error && <div className="notice">Receipt not found — check the link from your email.</div>}
        {!error && data == null && <div className="acct-list"><div className="acct-row skeleton" /></div>}
        {data != null && (
          <div className="cocard">
            <h3 className="coh" style={{ textAlign: 'center' }}>Payment details</h3>
            <ReceiptRecord name="Date">{fmtDate(data.topup_at || data.created_at)}</ReceiptRecord>

            {/* Topup-specific fields */}
            {!isEsim && <ReceiptRecord name="Operator">{OPERATOR_NAMES[carrierKey] || data.carrier || '—'}</ReceiptRecord>}
            {!isEsim && data.phone && <ReceiptRecord name="Phone Number">{data.phone}</ReceiptRecord>}

            {data.product && <ReceiptRecord name="Product">{data.product}</ReceiptRecord>}

            {/* Topup amounts in THB */}
            {!isEsim && data.sent_amount != null && <ReceiptRecord name="Topup Amount">{data.sent_amount} {data.sent_currency || 'THB'}</ReceiptRecord>}
            {!isEsim && <ReceiptRecord name="Processing Fee">{data.fee_amount || 0} THB</ReceiptRecord>}
            {!isEsim && data.total_amount != null && <ReceiptRecord name="Total Paid"><b>{data.total_amount} THB</b></ReceiptRecord>}

            {/* eSIM amounts */}
            {isEsim && data.price_usd != null && <ReceiptRecord name="Price"><b>${Number(data.price_usd).toFixed(2)} USD</b></ReceiptRecord>}
            {isEsim && data.total_amount != null && data.settlement_currency !== 'USD' && <ReceiptRecord name="Charged">{data.total_amount} THB</ReceiptRecord>}

            <ReceiptRecord name="Payment Method">
              <span className="method-cell">
                {method.label}
                {method.logos.map((f) => (
                  <img key={f} src={`${ST}/images/payment-icons/${f}`} alt="" />
                ))}
              </span>
            </ReceiptRecord>
            <ReceiptRecord name="Payment Status"><StatusChip status={data.payment_status || data.status} /></ReceiptRecord>
            {!isEsim && <ReceiptRecord name="Top-up Status"><StatusChip status={data.topup_status || '—'} /></ReceiptRecord>}
            {isEsim && <ReceiptRecord name="Purchase Status"><StatusChip status={data.purchase_status || '—'} /></ReceiptRecord>}
            <ReceiptRecord name="Order ID"><span className="mono-sm">{data.short_id}</span></ReceiptRecord>
          </div>
        )}

        <div className="cofoot">
          Questions about this payment?{' '}
          <a href={`/contact?orderId=${encodeURIComponent(data ? data.id : id)}&type=${data ? (isEsim ? 'esim' : 'topup') : ''}&summary=${encodeURIComponent(data ? (data.product || '') : '')}&status=${encodeURIComponent(data ? data.status || '' : '')}&date=${encodeURIComponent(data ? (data.created_at || '') : '')}`}>
            Contact support
          </a>
        </div>
      </div>
      <SiteFooterV2 currency={currency} onCurrency={onCurrency} />
    </div>
  );
}


///////////////////////////////////////////////////////////////////////////////
// SavedPaymentMethods — view/remove/default saved Omise cards on /settings
///////////////////////////////////////////////////////////////////////////////
const MAX_SAVED_CARDS = 5;
const BRAND_ICONS = { Visa: 'visa.png', MasterCard: 'master.png', JCB: 'jcb.png' };

function SavedPaymentMethods({ me, onMe }) {
  const [busy, setBusy] = useState(null); // card id being acted on
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addBusy, setAddBusy] = useState(false);

  const cust = me && me.omiseCustomer;
  const cards = useMemo(() => {
    if (!cust || !cust.cards || !cust.cards.data) return [];
    return cust.cards.data
      .filter((c) => c && c.id && !c.deleted)
      .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  }, [cust]);

  function updateMe(omiseCustomer) {
    if (onMe) onMe((prev) => ({ ...prev, omiseCustomer }));
  }

  async function removeCard(cardId) {
    if (!confirm('Remove this card?')) return;
    setBusy(cardId);
    setError(null);
    try {
      const d = await postJson('/api/payment/omise/removeSavedCard', { id: cardId });
      if (d.omiseCustomer) updateMe(d.omiseCustomer);
    } catch (err) {
      setError(err.message || 'Failed to remove card');
    }
    setBusy(null);
  }

  async function setDefault(cardId) {
    setBusy(cardId);
    setError(null);
    try {
      const d = await postJson('/api/payment/omise/setDefaultCard', { id: cardId });
      if (d.omiseCustomer) updateMe(d.omiseCustomer);
    } catch (err) {
      setError(err.message || 'Failed to set default');
    }
    setBusy(null);
  }

  async function addCard(e) {
    e.preventDefault();
    setAddError(null);

    const form = e.target;
    const number = form.number.value.replace(/\s/g, '');
    const month = form.month.value;
    const year = form.year.value;
    const cvv = form.cvv.value;
    const name = form.cardname.value.trim();

    if (!number || !month || !year || !cvv || !name) {
      setAddError('All fields are required.');
      return;
    }

    setAddBusy(true);
    try {
      // Tokenize via Omise.js
      if (!window.Omise) throw new Error('Card check failed. Disable script blockers and reload.');
      if (!MT.omisePublicKey) throw new Error('Card payments are unavailable right now.');
      window.Omise.setPublicKey(MT.omisePublicKey);

      const token = await new Promise((resolve, reject) => {
        window.Omise.createToken('card', {
          number, expiration_month: month, expiration_year: year,
          security_code: cvv, name,
        }, (status, tok) => {
          if (status === 200 && tok && tok.id) return resolve(tok);
          reject(new Error(tok && tok.message || 'Card was declined.'));
        });
      });

      const d = await postJson('/api/payment/omise/addNewCard', {
        token: token.id,
        card: token.card.id,
        uri: window.location.href,
      });

      if (d.charge && d.charge.authorize_uri) {
        // 3DS — redirect for verification
        window.location.href = d.charge.authorize_uri;
        return;
      }
      if (d.omiseCustomer) {
        updateMe(d.omiseCustomer);
        setAdding(false);
        form.reset();
      }
    } catch (err) {
      setAddError(err.message || 'Failed to add card.');
    }
    setAddBusy(false);
  }

  if (!me) return null;

  const defaultCard = cust && cust.default_card;
  const canAdd = cards.length < MAX_SAVED_CARDS;

  return (
    <div className="cocard" style={{ marginTop: 20 }}>
      <h3 className="coh">Saved payment methods</h3>
      <div style={{ padding: '12px 16px' }}>
        {error && <div className="notice" style={{ marginBottom: 12 }}>{error}</div>}

        {cards.length === 0 && !adding && (
          <p style={{ color: 'var(--muted)', margin: '0 0 12px' }}>
            No saved cards yet. Cards are saved automatically when you make a payment, or you can add one below.
          </p>
        )}

        {cards.length > 0 && (
          // ph-no-capture: keep cardholder name and last 4 out of PostHog autocapture (#351)
          <div className="pm-cards ph-no-capture">
            {cards.map((c) => {
              const isDefault = c.id === defaultCard;
              const icon = BRAND_ICONS[c.brand];
              return (
                <div key={c.id} className={`pm-card${isDefault ? ' pm-default' : ''}`}>
                  <div className="pm-card-info">
                    {icon && <img src={`${ST}/images/payment-icons/${icon}`} alt={c.brand} style={{ height: 20, marginRight: 8 }} />}
                    <span className="pm-brand">{c.brand}</span>
                    <span className="pm-digits">•••• {c.last_digits}</span>
                    <span className="pm-exp">{String(c.expiration_month).padStart(2, '0')}/{String(c.expiration_year).slice(-2)}</span>
                    {isDefault && <span className="pm-badge">Default</span>}
                  </div>
                  {c.name && <div className="pm-name">{c.name}</div>}
                  <div className="pm-actions">
                    {!isDefault && (
                      <button type="button" className="btn small outline" disabled={busy === c.id}
                        onClick={() => setDefault(c.id)}>
                        Set default
                      </button>
                    )}
                    <button type="button" className="btn small outline danger" disabled={busy === c.id}
                      onClick={() => removeCard(c.id)}>
                      {busy === c.id ? 'Removing…' : 'Remove'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!adding && canAdd && (
          <button type="button" className="btn small" style={{ marginTop: 8 }}
            onClick={() => { setAdding(true); setAddError(null); }}>
            + Add a card
          </button>
        )}
        {!canAdd && !adding && (
          <p style={{ color: 'var(--muted)', fontSize: 13, marginTop: 8 }}>
            Maximum of {MAX_SAVED_CARDS} saved cards reached. Remove one to add another.
          </p>
        )}

        {adding && (
          <form onSubmit={addCard} style={{ marginTop: 12 }}>
            <div className="settings-field">
              <label>Cardholder name</label>
              <input name="cardname" className="coinput" placeholder="Name on card" autoComplete="cc-name" />
            </div>
            <div className="settings-field">
              <label>Card number</label>
              <input name="number" className="coinput" placeholder="1234 5678 9012 3456" autoComplete="cc-number" inputMode="numeric" />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div className="settings-field" style={{ flex: 1 }}>
                <label>Month</label>
                <input name="month" className="coinput" placeholder="MM" maxLength={2} autoComplete="cc-exp-month" inputMode="numeric" />
              </div>
              <div className="settings-field" style={{ flex: 1 }}>
                <label>Year</label>
                <input name="year" className="coinput" placeholder="YYYY" maxLength={4} autoComplete="cc-exp-year" inputMode="numeric" />
              </div>
              <div className="settings-field" style={{ flex: 1 }}>
                <label>CVV</label>
                <input name="cvv" className="coinput" placeholder="•••" maxLength={4} autoComplete="cc-csc" inputMode="numeric" type="password" />
              </div>
            </div>
            {addError && <div className="notice" style={{ marginTop: 8 }}>{addError}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button type="submit" className="btn small" disabled={addBusy}>{addBusy ? 'Adding…' : 'Add card'}</button>
              <button type="button" className="btn small outline" onClick={() => { setAdding(false); setAddError(null); }}>Cancel</button>
            </div>
            <p style={{ color: 'var(--muted)', fontSize: 12, marginTop: 8 }}>
              A small test charge (฿20) will be made and refunded to verify your card.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// MessengerConnect — connect/disconnect Telegram & WhatsApp from /settings
///////////////////////////////////////////////////////////////////////////////

function MessengerConnect({ me }) {
  const [channels, setChannels] = useState(null); // null = loading
  const [links, setLinks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch connected channels on mount
  useEffect(() => {
    if (!me) return;
    fetch('/api/connect-messenger/status', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setChannels(d.channels || []))
      .catch(() => setChannels([]));
  }, [me]);

  function isConnected(ch) {
    return channels && channels.some((c) => c.channel === ch);
  }

  async function generateLinks() {
    setLoading(true);
    setError(null);
    try {
      const d = await postJson('/api/connect-messenger/token', {});
      setLinks(d.links);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  async function disconnect(channel) {
    if (!confirm(`Disconnect ${channel === 'telegram' ? 'Telegram' : 'WhatsApp'}?`)) return;
    try {
      await postJson('/api/connect-messenger/disconnect', { channel });
      setChannels((prev) => (prev || []).filter((c) => c.channel !== channel));
      setLinks(null);
    } catch (err) {
      setError(err.message);
    }
  }

  if (!me || channels === null) return null;

  const telegramConnected = isConnected('telegram');
  const whatsappConnected = isConnected('whatsapp');
  const bothConnected = telegramConnected && whatsappConnected;

  return (
    <div className="cocard" style={{ marginTop: 20 }}>
      <h3 className="coh">Connected Messengers</h3>
      <div style={{ padding: '12px 16px' }}>
        <p style={{ fontSize: '.85rem', color: '#666', marginBottom: 12 }}>
          Connect your Telegram or WhatsApp to check balances, view orders, and top up from chat.
        </p>

        {/* Connected channels */}
        {(telegramConnected || whatsappConnected) && (
          <div style={{ marginBottom: 12 }}>
            {telegramConnected && (
              <div className="messenger-row">
                <span className="messenger-icon" style={{ background: '#0088cc' }}>T</span>
                <span className="messenger-label">Telegram connected</span>
                <button className="btn tiny alert" onClick={() => disconnect('telegram')}>Disconnect</button>
              </div>
            )}
            {whatsappConnected && (
              <div className="messenger-row">
                <span className="messenger-icon" style={{ background: '#25D366' }}>W</span>
                <span className="messenger-label">WhatsApp connected</span>
                <button className="btn tiny alert" onClick={() => disconnect('whatsapp')}>Disconnect</button>
              </div>
            )}
          </div>
        )}

        {/* Connect buttons */}
        {!bothConnected && !links && (
          <button className="btn small" onClick={generateLinks} disabled={loading}>
            {loading ? 'Generating link...' : 'Connect a Messenger'}
          </button>
        )}

        {/* Deep links */}
        {links && !bothConnected && (
          <div className="messenger-links">
            <p style={{ fontSize: '.8rem', color: '#999', marginBottom: 8 }}>
              Tap a link below to open the app and connect your account. The link expires in 15 minutes.
            </p>
            {!telegramConnected && (
              <a href={links.telegram} target="_blank" rel="noreferrer" className="messenger-link-btn telegram">
                <span className="messenger-icon" style={{ background: '#0088cc' }}>T</span>
                Connect Telegram
              </a>
            )}
            {!whatsappConnected && (
              <a href={links.whatsapp} target="_blank" rel="noreferrer" className="messenger-link-btn whatsapp">
                <span className="messenger-icon" style={{ background: '#25D366' }}>W</span>
                Connect WhatsApp
              </a>
            )}
            <p style={{ fontSize: '.75rem', color: '#aaa', marginTop: 8 }}>
              After tapping, send the message in the app to complete the link.
              Then refresh this page to see the connection.
            </p>
          </div>
        )}

        {error && <div className="notice" style={{ marginTop: 8 }}>{error}</div>}
      </div>
    </div>
  );
}

///////////////////////////////////////////////////////////////////////////////
// /settings — account settings (profile, password change)
///////////////////////////////////////////////////////////////////////////////

///////////////////////////////////////////////////////////////////////////////
// /top-me-up — create and manage reusable Top Me Up links (see
// lib/routes/api/topup-requests.js). The payer-facing page is
// /r/:shortId (TopupPay.jsx).
///////////////////////////////////////////////////////////////////////////////
// Min/max/presets per currency come from GET /api/topup-requests/config
// (TMU_FALLBACK_LIMITS only covers the moment before it answers).
const TMU_DEFAULT_AMOUNTS = {
  THB: { fixed: 500, min: 100, max: 2000 },
  USD: { fixed: 10, min: 5, max: 50 },
};
// Raw picked file, before resize — generous, just guards against the browser
// choking on something huge. The server's own cap (200KB, on the resized
// output) is the one that actually matters; see topup-requests.js.
const PHOTO_MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// Substitute {cap}/{count}/etc. placeholders in a translated string
function fill(s, vars) {
  return String(s).replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? vars[k] : m));
}

function tmuShareUrl(shortId, lang) {
  const base = `${window.location.origin}/r/${shortId}`;
  return lang && lang !== 'en' ? `${base}?l=${lang}` : base;
}

function TopupRequestRow({ request, onCancel, onCopied }) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLang, setShareLang] = useState(readLang());
  const url = tmuShareUrl(request.shortId, shareLang);

  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      onCopied && onCopied();
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }

  function share() {
    if (navigator.share) {
      navigator.share({ title: t('tmu_page_title'), text: `${t('tmu_page_title')} — ${request.receiverName.split(' ')[0]}`, url }).catch(() => {});
    } else {
      copy();
    }
  }

  function toggleExpand() {
    if (expanded) { setExpanded(false); return; }
    setExpanded(true);
    if (!detail) {
      fetch(`/api/topup-requests/${request.id}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => setDetail(d))
        .catch(() => {});
    }
  }

  const cur = request.currency === 'USD' ? 'USD' : 'THB';
  const amountLabel = request.amountType === 'fixed'
    ? fmtTmu(request.amount, cur)
    : `${fmtTmu(request.minAmount, cur)} – ${fmtTmu(request.maxAmount, cur)}`;
  // Payment rows: THB requests keep amountInBaht; USD rows carry the USD figure
  const paymentAmount = (p) => (cur === 'USD' ? (p.amountUsd != null ? p.amountUsd : p.amount) : p.amountInBaht);
  const iso = request.receiverCountryIso || (cur === 'THB' ? 'TH' : '');
  const e164 = String(request.receiverPhone || '').startsWith('+') ? request.receiverPhone : `+${String(request.receiverPhone || '').replace(/\D/g, '')}`;
  const topUpUrl = `/recharge?phone=${encodeURIComponent(e164)}${iso ? `&country=${iso}` : ''}`;

  return (
    <div className="acct-row static tmu-row">
      <div className="ar-main">
        <b>{amountLabel} {request.amountType === 'fixed' ? t('tmu_amount_fixed_suffix') : t('tmu_amount_open_suffix')}</b>
        <span className="ar-sub mono">{url}</span>
        {request.note && <div className="tmu-note tmu-note-sm">{request.note}</div>}
        {(cur === 'USD' || request.receiverPhoneVerified) && (
          <span className="ar-sub">
            {iso ? `${flagEmoji(iso)} ` : ''}{request.receiverPhone}{' '}
            {request.receiverPhoneVerified
              ? <span className="tag good"><span className="tag-ico">✓</span> {t('tmu_verified')}</span>
              : <span className="tag dim">{t('tmu_not_verified')}</span>}
          </span>
        )}
        <span className="ar-sub">{t('tmu_received')} <b>{fmtTmu(request.received || 0, cur)}</b></span>
      </div>
      <div className="ar-actions tmu-row-actions">
        {request.status === 'active'
          ? <span className="tag good"><span className="tag-ico">✓</span> {t('tmu_active')}</span>
          : <span className="tag dim"><span className="tag-ico">◯</span> {t('tmu_cancelled')}</span>}
        <select className="tmu-lang-select" value={shareLang} onChange={(e) => setShareLang(e.target.value)} aria-label={t('tmu_share_in')} title={t('tmu_share_in')}>
          {LANGS.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <button type="button" className="btn small" onClick={copy}>{copied ? t('tmu_copied') : t('tmu_copy_link')}</button>
        <button type="button" className="btn small btn-alt" onClick={share}>{t('tmu_share')}</button>
        {cur === 'USD' && <a className="btn small btn-alt" href={topUpUrl}>{t('tmu_top_up_number')}</a>}
        <button type="button" className="icon-btn" title={t('tmu_view_payments')} aria-label={t('tmu_view_payments')} onClick={toggleExpand}>{expanded ? '▲' : '▼'}</button>
        {request.status === 'active' && (
          <button type="button" className="icon-btn" title={t('tmu_cancel_link')} aria-label={t('tmu_cancel_link')} onClick={() => onCancel(request.id)}>{TrashIcon}</button>
        )}
      </div>
      {expanded && (
        <div className="tmu-payments">
          {!detail && <div className="modal-loading">{t('tmu_loading')}</div>}
          {detail && detail.payments && detail.payments.length === 0 && <div className="ar-sub">{t('tmu_no_payments')}</div>}
          {detail && detail.payments && detail.payments.map((p) => (
            <div key={p.id} className="tmu-payment-row">
              <span>{p.payerEmail || t('tmu_anonymous')}</span>
              <b className="mono">+{fmtTmu(paymentAmount(p), cur)}</b>
              <span className="ar-sub">{fmtDate(p.createdAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function TopMeUpPage({ me, currency, onCurrency }) {
  const t = useT();
  useRequireAuth(me, '/top-me-up');
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(false);

  const [cfg, setCfg] = useState({ globalEnabled: false, limits: TMU_FALLBACK_LIMITS });
  const [amountType, setAmountType] = useState('fixed');
  const [amount, setAmount] = useState('500');
  const [minAmount, setMinAmount] = useState('100');
  const [maxAmount, setMaxAmount] = useState('2000');
  const [note, setNote] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [receiverPhone, setReceiverPhone] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [justCreated, setJustCreated] = useState(null);

  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoError, setPhotoError] = useState(null);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    if (!me) return;
    setReceiverName(`${me.firstName || ''} ${me.lastName || ''}`.trim());
    setPhotoUrl(me.topupPhotoUrl || null);
  }, [me]);

  useEffect(() => {
    if (!me) return;
    fetch('/api/topup-requests/config')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.limits && d.limits.THB && d.limits.USD) setCfg({ globalEnabled: !!d.globalEnabled, limits: d.limits });
      })
      .catch(() => { /* keep THB-only fallback */ });
  }, [me]);

  // Currency follows the receiver number: Thai -> THB, any other country -> USD
  // (only when the server has global numbers switched on).
  const phoneKind = tmuPhoneKind(receiverPhone);
  const globalBlocked = phoneKind === 'global' && !cfg.globalEnabled;
  const cur = cfg.globalEnabled && phoneKind === 'global' ? 'USD' : 'THB';
  const lim = cfg.limits[cur];
  const limRange = `${fmtTmu(lim.min, cur)}–${fmtTmu(lim.max, cur)}`;

  // Switching currency swaps in that currency's default amounts
  const prevCur = React.useRef('THB');
  useEffect(() => {
    if (prevCur.current === cur) return;
    prevCur.current = cur;
    const d = TMU_DEFAULT_AMOUNTS[cur];
    setAmount(String(d.fixed));
    setMinAmount(String(d.min));
    setMaxAmount(String(d.max));
  }, [cur]);

  // Square-crop and downscale to a small JPEG before upload — keeps the
  // stored data URI (in RethinkDB, no object storage in this app yet) small.
  function resizeImage(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(new Error('Could not read that file'));
      reader.onload = () => {
        const img = new Image();
        img.onerror = () => reject(new Error('Could not read that image'));
        img.onload = () => {
          const size = 240;
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
          const ctx2d = canvas.getContext('2d');
          const scale = Math.max(size / img.width, size / img.height);
          const w = img.width * scale;
          const h = img.height * scale;
          ctx2d.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.82));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    });
  }

  async function onPhotoChange(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { setPhotoError('Please choose an image file'); return; }
    if (file.size > PHOTO_MAX_UPLOAD_BYTES) {
      setPhotoError(`That file is too large (${Math.round(file.size / (1024 * 1024))}MB) — please choose one under ${PHOTO_MAX_UPLOAD_BYTES / (1024 * 1024)}MB`);
      return;
    }
    setPhotoError(null);
    setPhotoBusy(true);
    try {
      const dataUri = await resizeImage(file);
      const d = await postJson('/api/topup-requests/photo', { dataUri });
      setPhotoUrl(d.topupPhotoUrl);
    } catch (err) {
      setPhotoError(err.message || 'Could not upload photo');
    }
    setPhotoBusy(false);
  }
  // Photo validation errors above stay in English (client-side guard, rare
  // path) — everything user-facing below is translated.

  async function removePhoto() {
    setPhotoBusy(true);
    try {
      await fetch('/api/topup-requests/photo', { method: 'DELETE' });
      setPhotoUrl(null);
    } catch (e) { /* leave it */ }
    setPhotoBusy(false);
  }

  function loadRequests() {
    fetch('/api/topup-requests')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => setRequests(d.requests || []))
      .catch(() => setError(true));
  }

  useEffect(() => {
    if (!me) return;
    loadRequests();
  }, [me]);

  async function createRequest(e) {
    e.preventDefault();
    setCreateError(null);
    setCreateBusy(true);
    try {
      const body = { receiverName: receiverName.trim(), receiverPhone: receiverPhone.trim(), amountType, note: note.trim() };
      if (amountType === 'fixed') body.amount = Number(amount);
      else { body.minAmount = Number(minAmount); body.maxAmount = Number(maxAmount); }
      const created = await postJson('/api/topup-requests', body);
      setJustCreated(created);
      setNote('');
      loadRequests();
    } catch (err) {
      setCreateError(err.message);
    }
    setCreateBusy(false);
  }

  // postJson always POSTs; cancel needs PATCH, so a tiny local helper.
  async function patchJson(url, body) {
    const r = await fetch(url, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  }

  async function doCancel(id) {
    if (!window.confirm(t('tmu_cancel_confirm'))) return;
    try {
      await patchJson(`/api/topup-requests/${id}`, { status: 'cancelled' });
      loadRequests();
    } catch (e) { /* leave it */ }
  }

  const rail = (
    <div className="railcard trust">
      <h4>{t('tmu_how_it_works')}</h4>
      {t('tmu_how_it_works_body')}
    </div>
  );

  const activeCount = requests ? requests.filter((r) => r.status === 'active').length : 0;

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title={t('tmu_page_title')} sub={t('tmu_page_sub')} rail={rail}>
      {justCreated && (
        <div className="notice" style={{ background: 'rgba(46,204,113,.1)', color: '#27ae60', border: '1px solid rgba(46,204,113,.3)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
          {t('tmu_link_created')} <b className="mono">{tmuShareUrl(justCreated.shortId)}</b>
        </div>
      )}

      <div className="cocard">
        <h3 className="coh">{t('tmu_your_profile')}</h3>
        <p className="cohint" style={{ marginTop: -6, marginBottom: 14 }}>{t('tmu_profile_sub')}</p>
        <div className="tmu-photo-row">
          <div className="tmu-photo-wrap">
            {photoUrl
              ? <img className="user-avatar tmu-avatar-lg" src={photoUrl} alt="" />
              : <span className="user-avatar user-avatar-initials tmu-avatar-lg">{((receiverName || '?').trim()[0] || '?').toUpperCase()}</span>}
            <button
              type="button" className="tmu-photo-edit" title={t('tmu_remove_photo')} aria-label={t('tmu_remove_photo')}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              disabled={photoBusy}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={onPhotoChange} />
          </div>
          <div>
            <p className="ar-sub" style={{ margin: 0 }}>
              {photoBusy ? t('tmu_photo_uploading') : photoUrl ? t('tmu_photo_set') : t('tmu_photo_required')}
            </p>
            {photoUrl && !photoBusy && (
              <button type="button" className="linkish" style={{ fontSize: 12 }} onClick={removePhoto}>{t('tmu_remove_photo')}</button>
            )}
            {photoError && <div className="notice bad payerr" style={{ marginTop: 6 }}>{photoError}</div>}
          </div>
        </div>
      </div>

      <div className="cocard">
        <h3 className="coh">{t('tmu_new_request')}</h3>
        <form onSubmit={createRequest}>
          <div className="tmu-form-row">
            <label htmlFor="tmuName">{t('tmu_your_name')}</label>
            <input id="tmuName" className="coinput" type="text" value={receiverName}
              onChange={(e) => setReceiverName(e.target.value)} placeholder={t('tmu_your_name')} required />
          </div>
          <div className="tmu-form-row">
            <label htmlFor="tmuPhone">{t('tmu_your_phone')}</label>
            <input id="tmuPhone" className="coinput" type="tel" value={receiverPhone}
              onChange={(e) => setReceiverPhone(e.target.value)} placeholder={cfg.globalEnabled ? t('tmu_phone_placeholder') : '08X XXX XXXX'} required />
            <div className="cohint">{t('tmu_phone_hint')}</div>
            {cfg.globalEnabled && <div className="cohint">{t('tmu_phone_examples')}</div>}
            {globalBlocked && <div className="notice bad payerr" style={{ marginTop: 6 }}>{t('tmu_global_off')}</div>}
            {cfg.globalEnabled && <TmuPhoneVerify phone={receiverPhone} />}
            {cfg.globalEnabled && phoneKind !== 'empty' && (
              <div className="tmu-cur-line">{fill(t(cur === 'USD' ? 'tmu_requests_in_usd' : 'tmu_requests_in_thb'), { range: limRange })}</div>
            )}
          </div>

          <div className="tmu-form-row">
            <span className="cohint" style={{ display: 'block', marginBottom: 8 }}>{t('tmu_amount_label')}</span>
            <div className="tmu-amounts">
              <button type="button" className={`chip ${amountType === 'fixed' ? 'on' : ''}`} onClick={() => setAmountType('fixed')}>{t('tmu_amount_fixed')}</button>
              <button type="button" className={`chip ${amountType === 'open' ? 'on' : ''}`} onClick={() => setAmountType('open')}>{t('tmu_amount_open')}</button>
            </div>
          </div>

          {amountType === 'fixed' ? (
            <div className="tmu-form-row">
              <label htmlFor="tmuAmount">{t(cur === 'USD' ? 'tmu_requested_amount_usd' : 'tmu_requested_amount')}</label>
              <input id="tmuAmount" className="coinput" type="number" min={lim.min} max={lim.max} step={cur === 'USD' ? '0.01' : undefined}
                value={amount} onChange={(e) => setAmount(e.target.value)} required />
              {cfg.globalEnabled && lim.presets && lim.presets.length > 0 && (
                <div className="tmu-amounts tmu-presets">
                  {lim.presets.map((v) => (
                    <button type="button" key={v} className={`chip ${Number(amount) === v ? 'on' : ''}`} onClick={() => setAmount(String(v))}>{fmtTmu(v, cur)}</button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="tmu-form-row tmu-form-row-split">
              <div>
                <label htmlFor="tmuMin">{t(cur === 'USD' ? 'tmu_min_amount_usd' : 'tmu_min_amount')}</label>
                <input id="tmuMin" className="coinput" type="number" min={lim.min} max={lim.max} step={cur === 'USD' ? '0.01' : undefined}
                  value={minAmount} onChange={(e) => setMinAmount(e.target.value)} required />
              </div>
              <div>
                <label htmlFor="tmuMax">{t(cur === 'USD' ? 'tmu_max_amount_usd' : 'tmu_max_amount')}</label>
                <input id="tmuMax" className="coinput" type="number" min={lim.min} max={lim.max} step={cur === 'USD' ? '0.01' : undefined}
                  value={maxAmount} onChange={(e) => setMaxAmount(e.target.value)} required />
              </div>
            </div>
          )}

          <div className="tmu-form-row">
            <label htmlFor="tmuNote">{t('tmu_note_label')}</label>
            <input id="tmuNote" className="coinput" type="text" value={note}
              onChange={(e) => setNote(e.target.value)} placeholder={t('tmu_note_placeholder')} maxLength={300} required />
            <div className="cohint">{t('tmu_note_required_hint')}</div>
          </div>

          <div className="cohint">
            {cur === 'USD'
              ? fill(t('tmu_cap_hint_usd'), { range: limRange })
              : fill(t('tmu_cap_hint'), { cap: fmtBaht(lim.max) })}
          </div>

          {!photoUrl && (
            <div className="notice bad payerr" style={{ marginBottom: 12 }}>{t('tmu_photo_required_notice')}</div>
          )}
          {createError && <div className="notice bad payerr">{createError}</div>}

          <button type="submit" className="btn block" style={{ marginTop: 12 }} disabled={createBusy || !photoUrl || globalBlocked}>
            {createBusy ? t('tmu_creating') : t('tmu_create_link')}
          </button>
        </form>
      </div>

      <h3 className="acct-h3">{fill(t('tmu_your_links'), { count: activeCount })}</h3>
      {error && <div className="notice">{t('tmu_links_error')}</div>}
      {!error && requests == null && <div className="acct-list"><div className="acct-row skeleton" /></div>}
      {requests != null && requests.length === 0 && <div className="notice">{t('tmu_links_empty')}</div>}
      {requests != null && requests.length > 0 && (
        <div className="acct-list">
          {requests.map((r) => (
            <TopupRequestRow key={r.id} request={r} onCancel={doCancel} />
          ))}
        </div>
      )}
    </AccountShell>
  );
}

export function SettingsPage({ me, onMe, currency, onCurrency }) {
  useRequireAuth(me, '/settings');
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');

  // Invite link (only for eligible inviters — see /api/referrals)
  const [refLink, setRefLink] = useState(null);
  const [refCopied, setRefCopied] = useState(false);

  // Change email
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPw, setEmailPw] = useState('');
  const [emailMsg, setEmailMsg] = useState(null);
  const [emailSaving, setEmailSaving] = useState(false);

  // Password fields
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  // Timezone
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [tz, setTz] = useState('');
  const [tzEditing, setTzEditing] = useState(false);
  const [tzMsg, setTzMsg] = useState(null);
  const [tzSaving, setTzSaving] = useState(false);

  useEffect(() => {
    if (!me) return;
    fetch(`/api/auth/profile/${me.id}`, { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setProfile(d.profile);
        setFirst(d.profile.firstName || '');
        setLast(d.profile.lastName || '');
        setTz(d.profile.timezone || browserTz);
      })
      .catch(() => {});
    fetch('/api/referrals', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d && d.enabled && d.eligible && d.link) setRefLink(d.link); })
      .catch(() => {});
  }, [me]);

  if (me === undefined) return null;

  function copyRefLink() {
    navigator.clipboard.writeText(refLink).then(() => {
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2000);
    }).catch(() => {});
  }

  async function saveProfile(e) {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    try {
      const d = await postJson(`/api/auth/profile/${me.id}`, { email: profile.email, first: first.trim(), last: last.trim() });
      setProfile(d.profile);
      setMsg({ ok: true, text: 'Saved' });
    } catch (err) {
      setMsg({ ok: false, text: err.message });
    }
    setSaving(false);
  }

  async function requestEmailChange(e) {
    e.preventDefault();
    setEmailMsg(null);
    setEmailSaving(true);
    try {
      const d = await postJson('/api/auth/email-change/request', { email: newEmail.trim(), password: emailPw });
      setEmailMsg({ ok: true, text: d.message });
      setEmailPw('');
      setEmailEditing(false);
    } catch (err) {
      setEmailMsg({ ok: false, text: err.message });
    }
    setEmailSaving(false);
  }

  async function changePassword(e) {
    e.preventDefault();
    setPwMsg(null);
    if (!newPw || newPw.length < 6) { setPwMsg({ ok: false, text: 'Password must be at least 6 characters' }); return; }
    if (isCommonPassword(newPw)) { setPwMsg({ ok: false, text: 'That password is too common. Choose something less guessable.' }); return; }
    if (newPw !== confirmPw) { setPwMsg({ ok: false, text: 'Passwords do not match' }); return; }
    setPwSaving(true);
    try {
      await postJson(`/api/auth/profile/password/${me.id}`, { n: newPw, r: confirmPw });
      setPwMsg({ ok: true, text: 'Password updated' });
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setPwMsg({ ok: false, text: err.message });
    }
    setPwSaving(false);
  }

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Settings">
      {/* Profile section */}
      <div className="cocard" style={{ marginBottom: 20 }}>
        <h3 className="coh">Profile</h3>
        {!profile ? (
          <div className="acct-list"><div className="acct-row skeleton" /></div>
        ) : (
          <>
          {/* Email lives outside the profile <form> so its change form (a
              form of its own) can open right under the button */}
          <div style={{ padding: '12px 16px 0' }}>
            <div className="settings-field">
              <label>Email</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span className="settings-static">{profile.email || '—'}</span>
                {!emailEditing && (
                  <button type="button" className="btn small outline" onClick={() => { setEmailEditing(true); setEmailMsg(null); }}>Change email</button>
                )}
              </div>
            </div>
            {emailMsg && !emailEditing && <div className={`notice ${emailMsg.ok ? 'notice-ok' : ''}`}>{emailMsg.text}</div>}
            {emailEditing && (!profile.hasPassword ? (
              <div className="settings-field">
                <div className="notice">
                  Your account signs in with Google, Facebook or Apple, or doesn't have a password yet.
                  Set a password under <strong>Change password</strong> below, then come back here to change your email.
                  If you've lost access to your email, <a href="/support">contact support</a>.
                </div>
                <button type="button" className="btn small outline" onClick={() => setEmailEditing(false)}>Close</button>
              </div>
            ) : (
              <form onSubmit={requestEmailChange}>
                <div className="settings-field">
                  <label>New email</label>
                  <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="coinput" autoComplete="email" required />
                </div>
                <div className="settings-field">
                  <label>Current password</label>
                  <input type="password" value={emailPw} onChange={(e) => setEmailPw(e.target.value)} className="coinput" autoComplete="current-password" required />
                </div>
                <div className="cohint">We'll email a confirmation link to the new address. Your email only changes after you click it.</div>
                {emailMsg && <div className={`notice ${emailMsg.ok ? 'notice-ok' : ''}`}>{emailMsg.text}</div>}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="submit" className="btn small" disabled={emailSaving}>{emailSaving ? 'Sending…' : 'Send confirmation link'}</button>
                  <button type="button" className="btn small outline" onClick={() => { setEmailEditing(false); setEmailMsg(null); }}>Cancel</button>
                </div>
              </form>
            ))}
          </div>
          <form onSubmit={saveProfile} style={{ padding: '0 16px 12px' }}>
            <div className="settings-field">
              <label>First name</label>
              <input type="text" value={first} onChange={(e) => setFirst(e.target.value)} className="coinput" />
            </div>
            <div className="settings-field">
              <label>Last name</label>
              <input type="text" value={last} onChange={(e) => setLast(e.target.value)} className="coinput" />
            </div>
            <div className="settings-field inline">
              <label>Member since</label>
              <span className="settings-static">{profile.createdAt}{profile.memberYears ? ` (${profile.memberYears} years)` : ''}</span>
            </div>
            <div className="settings-field inline">
              <label>Email verified</label>
              <span className="settings-static">{profile.verifiedEmail ? 'Yes' : 'No'}</span>
            </div>
            {profile.isTrusted && (
              <div className="settings-field inline">
                <label>Account status</label>
                <span className="settings-static" style={{ color: 'var(--success, #43AC6A)' }}>Trusted</span>
              </div>
            )}
            <div className="settings-field inline">
              <label>Orders</label>
              <span className="settings-static">{profile.paidOrders || 0}</span>
            </div>
            <div className="settings-field">
              <label>{refLink ? 'Your invite link' : 'Refer a friend'}</label>
              {refLink ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                  <a href={refLink} className="settings-static mono-sm" style={{ wordBreak: 'break-all' }}>{refLink}</a>
                  <button type="button" className="btn small outline" onClick={copyRefLink}>{refCopied ? 'Copied!' : 'Copy'}</button>
                  <a href="/account/referrals" className="cohint">Invites &amp; rewards →</a>
                </div>
              ) : (
                <span className="settings-static"><a href="/account/referrals">Your invite link &amp; rewards →</a></span>
              )}
            </div>
            {msg && <div className={`notice ${msg.ok ? 'notice-ok' : ''}`}>{msg.text}</div>}
            <button type="submit" className="btn small" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </form>
          </>
        )}
      </div>

      {/* Saved payment methods */}
      <SavedPaymentMethods me={me} onMe={onMe} />

      {/* Password section */}
      <div className="cocard" style={{ marginTop: 20 }}>
        <h3 className="coh">Change password</h3>
        <form onSubmit={changePassword} style={{ padding: '12px 16px' }}>
          <div className="settings-field">
            <label>New password</label>
            <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="coinput" autoComplete="new-password" />
          </div>
          <div className="settings-field">
            <label>Confirm password</label>
            <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="coinput" autoComplete="new-password" />
          </div>
          {pwMsg && <div className={`notice ${pwMsg.ok ? 'notice-ok' : ''}`}>{pwMsg.text}</div>}
          <button type="submit" className="btn small" disabled={pwSaving}>{pwSaving ? 'Updating…' : 'Change password'}</button>
        </form>
      </div>

      {/* Timezone section */}
      <div className="cocard" style={{ marginTop: 20 }}>
        <h3 className="coh">Time zone</h3>
        <div style={{ padding: '12px 16px' }}>
          {!tzEditing ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span>{(tz || browserTz).replace(/_/g, ' ')}{!profile?.timezone ? ' (detected)' : ''}</span>
              <button type="button" className="btn small outline" onClick={() => { setTzEditing(true); setTzMsg(null); }}>Change</button>
            </div>
          ) : (
            <>
              <div className="settings-field">
                <label>Time zone</label>
                <select className="coinput" value={tz} onChange={(e) => setTz(e.target.value)}>
                  {Intl.supportedValuesOf('timeZone').map((z) => (
                    <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>
              <div className="settings-field">
                <label>Preview</label>
                <span className="settings-static">{new Date().toLocaleTimeString([], { timeZone: tz, hour: '2-digit', minute: '2-digit', timeZoneName: 'short' })}</span>
              </div>
              {tzMsg && <div className={`notice ${tzMsg.ok ? 'notice-ok' : ''}`}>{tzMsg.text}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="btn small" disabled={tzSaving} onClick={async () => {
                  setTzSaving(true);
                  setTzMsg(null);
                  try {
                    const d = await postJson(`/api/auth/profile/${me.id}`, { email: me.email, first: first.trim() || me.firstName || 'User', last: last.trim() || me.lastName || 'User', timezone: tz });
                    setProfile(d.profile);
                    setTzMsg({ ok: true, text: 'Time zone saved' });
                    setTzEditing(false);
                  } catch (err) {
                    setTzMsg({ ok: false, text: err.message });
                  }
                  setTzSaving(false);
                }}>{tzSaving ? 'Saving…' : 'Save'}</button>
                <button type="button" className="btn small outline" onClick={() => { setTzEditing(false); setTz(profile?.timezone || browserTz); setTzMsg(null); }}>Cancel</button>
                <button type="button" className="btn small outline" onClick={() => setTz(browserTz)} style={{ marginLeft: 'auto' }}>Detect from browser</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Connected accounts */}
      {profile && profile.facebook && (
        <div className="cocard" style={{ marginTop: 20 }}>
          <h3 className="coh">Connected accounts</h3>
          <div style={{ padding: '12px 16px' }}>Facebook connected</div>
        </div>
      )}

      {/* Connected messengers */}
      <MessengerConnect me={me} />
    </AccountShell>
  );
}


///////////////////////////////////////////////////////////////////////////////
// /support — help & support links
///////////////////////////////////////////////////////////////////////////////

export function SupportPage({ me, currency, onCurrency }) {
  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency} title="Support" hideSupportCard>
      <div className="cocard">
        <h3 className="coh">Help & Support</h3>
        <div className="support-links">
          <a href="https://help.mobiletopup.com" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">?</span>
            <div>
              <strong>Help Center</strong>
              <p>Guides, FAQs, and how-tos</p>
            </div>
          </a>
          <a href="/contact" className="support-link">
            <span className="support-icon">!</span>
            <div>
              <strong>Contact Us</strong>
              <p>Send us a message</p>
            </div>
          </a>
          <a href="https://wa.me/66926200610?text=Hi" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">W</span>
            <div>
              <strong>WhatsApp</strong>
              <p>Chat with us on WhatsApp</p>
            </div>
          </a>
          <a href="https://t.me/mobiletopup_bot" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">T</span>
            <div>
              <strong>Telegram</strong>
              <p>Chat with us on Telegram</p>
            </div>
          </a>
          <a href="/orders" className="support-link">
            <span className="support-icon">#</span>
            <div>
              <strong>My Orders</strong>
              <p>Check order status or request a refund</p>
            </div>
          </a>
        </div>
      </div>
    </AccountShell>
  );
}


///////////////////////////////////////////////////////////////////////////////
// /contact — modernized contact form
///////////////////////////////////////////////////////////////////////////////

export function ContactPage({ me, currency, onCurrency }) {
  const params = new URLSearchParams(window.location.search);
  const orderIdParam = params.get('orderId') || '';
  const orderType = params.get('type') || '';
  const orderSummary = params.get('summary') || '';
  const orderStatus = params.get('status') || '';
  const orderDate = params.get('date') || '';
  const hasOrder = !!orderIdParam;

  const [name, setName] = useState((me && me.firstName) ? `${me.firstName} ${me.lastName || ''}`.trim() : '');
  const [email, setEmail] = useState((me && me.email) || '');
  const [message, setMessage] = useState('');

  // Populate name/email once auth resolves (me may be undefined on first render)
  useEffect(() => {
    if (me) {
      setName((prev) => prev || (me.firstName ? `${me.firstName} ${me.lastName || ''}`.trim() : ''));
      setEmail((prev) => prev || me.email || '');
    }
  }, [me]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  // A sent message is remembered for this tab, so a reload or remount of the
  // same /contact link shows it was received instead of a blank form (#334).
  const sentKey = `contact-sent:${orderIdParam || 'general'}`;
  const [success, setSuccess] = useState(() => {
    try {
      const at = sessionStorage.getItem(sentKey);
      return at ? `Message sent at ${new Date(Number(at)).toLocaleTimeString()} — we'll reply to your email shortly.` : null;
    } catch (e) { return null; }
  });
  const turnstile = useTurnstile('turnstile-contact');

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('All fields are required.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const turnstileToken = turnstile.token;
      const recaptcha = turnstileToken ? null : await recaptchaToken('contact');
      const res = await postJson('/api/contact', {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        orderId: orderIdParam || undefined,
        orderType: orderType || undefined,
        orderSummary: orderSummary || undefined,
        orderStatus: orderStatus || undefined,
        orderDate: orderDate || undefined,
        turnstileToken,
        recaptcha,
      });
      setSuccess(res.message || 'Message received — we\'ll reply to your email shortly.');
      setMessage('');
      try { sessionStorage.setItem(sentKey, String(Date.now())); } catch (e) { /* storage blocked */ }
      turnstile.reset(); // tokens are single-use; get a fresh one for a later send
    } catch (err) {
      setError(err.message || 'Something went wrong — please try again.');
      turnstile.reset();
    }
    setBusy(false);
  }

  return (
    <AccountShell me={me} currency={currency} onCurrency={onCurrency}
      title={hasOrder ? 'Get Help' : 'Contact Us'} hideSupportCard
    >
      <a href={hasOrder ? '/orders' : '/support'} className="dep-back">&larr; {hasOrder ? 'Back to orders' : 'Back to support'}</a>

      {/* Order details card (when linked from an order) */}
      {hasOrder && (
        <div className="cocard" style={{ marginBottom: 20 }}>
          <h3 className="coh">Order details</h3>
          <div style={{ padding: '12px 16px', fontSize: 14, lineHeight: 1.7 }}>
            <div><strong>Order ID:</strong> <code style={{ fontSize: 12, background: '#f4f4f4', padding: '2px 6px', borderRadius: 4 }}>{orderType === 'esim' ? 'ESM-' : 'ORD-'}{orderIdParam.replace(/-/g, '').slice(0, 8).toUpperCase()}</code></div>
            {orderType && <div><strong>Type:</strong> {orderType === 'esim' ? 'eSIM' : 'Topup'}</div>}
            {orderSummary && <div><strong>Product:</strong> {orderSummary}</div>}
            {orderStatus && <div><strong>Status:</strong> {orderStatus}</div>}
            {orderDate && <div><strong>Date:</strong> {fmtDate(orderDate)}</div>}
          </div>
        </div>
      )}

      <div className="cocard">
        <h3 className="coh" style={{ textAlign: 'center' }}>
          {hasOrder ? 'Describe your issue' : 'Send us a message'}
        </h3>
        {success && (
          <div style={{ margin: 16 }}>
            <div className="notice notice-ok">{success}</div>
            <button type="button" className="btn small outline" style={{ marginTop: 10 }}
              onClick={() => {
                try { sessionStorage.removeItem(sentKey); } catch (e) { /* storage blocked */ }
                setSuccess(null);
              }}
            >Send another message</button>
          </div>
        )}
        {/* The form stays mounted (hidden) after a send so the Turnstile
            widget inside it survives for "Send another message" */}
          <form onSubmit={submit} style={{ padding: '12px 16px', display: success ? 'none' : undefined }}>
            <div className="settings-field">
              <label>Name</label>
              <input className="coinput" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div className="settings-field">
              <label>Email</label>
              <input className="coinput" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" />
            </div>
            <div className="settings-field">
              <label>{hasOrder ? 'What went wrong?' : 'Message'}</label>
              <textarea className="coinput" rows="5" value={message} onChange={(e) => setMessage(e.target.value)}
                placeholder={hasOrder ? 'Describe the issue with this order...' : 'How can we help?'}
                style={{ resize: 'vertical' }}
              />
            </div>
            <div id="turnstile-contact" />
            {error && <div className="notice payerr">{error}</div>}
            <button type="submit" className="btn block colarge" disabled={busy} style={{ marginTop: 10 }}>
              {busy ? 'Sending…' : hasOrder ? 'Submit issue' : 'Send message'}
            </button>
          </form>
      </div>

      <div className="cocard" style={{ marginTop: 20 }}>
        <h3 className="coh" style={{ textAlign: 'center' }}>Other ways to reach us</h3>
        <div className="support-links">
          <a href="https://wa.me/66926200610?text=Hi" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">W</span>
            <div>
              <strong>WhatsApp</strong>
              <p>Chat with us on WhatsApp</p>
            </div>
          </a>
          <a href="https://t.me/mobiletopup_bot" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">T</span>
            <div>
              <strong>Telegram</strong>
              <p>Chat with us on Telegram</p>
            </div>
          </a>
          <a href="http://m.me/thaiprepaidcard" className="support-link" target="_blank" rel="noreferrer">
            <span className="support-icon">M</span>
            <div>
              <strong>Messenger</strong>
              <p>Chat on Facebook Messenger</p>
            </div>
          </a>
        </div>
      </div>
    </AccountShell>
  );
}
